// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { useParams, useNavigate, Link } from '@tanstack/react-router'
import { useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from './ui/button'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from './ui/select'
import { useAsset, useAssetProperties, useAssetTypes, useAssetTypeProperties, useUpdateAsset, useSetAssetPropertyValue, useUpdateAssetPropertyValue, useDeleteAssetPropertyValue, usePropertyDefinitions, usePropertyDefinitionsByUids, usePropertyDefinition, usePropertyEnumValues, useAssetWithParentChain, useRestoreAsset, refreshAssetProperties, queryKeys, type PropertyEnumValue } from '../hooks/useQueries'
import { apiClient } from '../api/client'
import { AssetName } from './ArchivedMarker'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { EntityPicture } from './EntityPicture'
import { AssetNotesSection } from './knowledge/AssetNotesSection'
import { useApi } from '../api/hooks'
import { $api } from '../api/queryClient'
import { type ApiSearchableSelectOption } from './ApiSearchableSelect'
import { AssetProperties } from './AssetProperties'
import { AssetDocuments } from './AssetDocuments'
import { AssetContracts } from './AssetContracts'
import { AssetClaimsCard } from './AssetClaimsCard'
import { AssetDecisions } from './assemblies/AssetDecisions'
import { AssetRentRollTab } from './AssetRentRollTab'
import { OccupationTab } from './occupation/OccupationTab'
import { EntityConversations } from './conversations/EntityConversations'
import { AssetIntakeAddress } from './conversations/AssetIntakeAddress'
import { AssetActivityLog } from './AssetActivityLog'
import { AssetDeleteDialog } from './AssetDeleteDialog'
import { AssetArchiveDialog } from './AssetArchiveDialog'
import { AssetMoveDialog } from './AssetMoveDialog'
import { useCan, useMemberKind, usePermissions, holdsPermission } from '../contexts/PermissionsContext'
import { assetTabAllowedForKind, shellForKind } from '../lib/sidebar-menus'
import type { Permission } from '../contexts/PermissionsContext'
import { AssetCompliance } from './AssetCompliance'
import { AssetDataTab } from './asset-data/AssetDataTab'
import { DATA_TAB_ASSET_TYPES, DATA_CATEGORY_ORDER } from './asset-data/baseProperties'
import { AssetAccessButton } from './AssetAccessButton'
import { AssetChildrenSection } from './assets/AssetChildrenSection'
import { AssetContacts } from './assets/asset-contacts'
import { OverflowMenu, OverflowMenuItem, OverflowMenuSeparator } from './shared/OverflowMenu'
import { AssetApprovalBanner } from './assets/AssetApprovalBanner'
import { AssetTypeTile } from './assets/AssetTypeTile'
import { getAssetHeaderSlots } from '../lib/asset-header-slots'
import { useAssetCoreRows } from '../hooks/useAssetCoreRows'
import { useLiveSubscription, useScopeLive } from '../contexts/LiveEventsContext'
import { useDynamicRefresh } from '../hooks/useDynamicRefresh'
import { BuildingPlanTab } from './plan/BuildingPlanTab'
import { AssetPlanViewer } from './AssetPlanViewer'
import { AgentRunsPanel } from './agent-runs/AgentRunsPanel'
import { lotBaseTypeCode } from '../lib/asset-type-tree'
// Site Map tab is lazy-loaded so Leaflet + Geoman (~150 KB gz) stay out of the
// main bundle and only load on building_group / parcel_land pages.
const SiteMapTab = lazy(() => import('./site-map/SiteMapTab'))
// Composite (shared-referential) plan viewer — spec 2026-07-27-08. Lazy so the
// SVG viewer + registration editor stay out of the main bundle.
const CompositePlanTab = lazy(() => import('./plan/CompositePlanTab'))
const CadastreMap = lazy(() => import('./parcel-cadastre/CadastreMap'))
import { AddressCompactDisplay } from './AddressCompactDisplay'
import { SiteAddressDisplay } from './SiteAddressDisplay'
import { AddressSearch, type AddressData } from './AddressSearch'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog'
import { AssetCapexPlans } from './capex/AssetCapexPlans'
import { EquipmentTab } from './equipment/EquipmentTab'
import { AssetPhotoTab } from './photo/AssetPhotoTab'
import { assetTypeCodeDescendsFrom, assetTypeDescendsFrom } from '../lib/asset-type-inheritance'
import { InterventionsTab } from './interventions/InterventionsTab'
import { CostOfOwnershipCard } from './equipment/CostOfOwnershipCard'
import { ComponentsCard } from './equipment/ComponentsCard'
import { MeterSupplyCard } from './meters/MeterSupplyCard'
import { MeterReadingsCard } from './meters/MeterReadingsCard'
import { NetworkPlansCard } from './plan/NetworkPlansCard'
import { NetworkSegmentsCard } from './plan/NetworkSegmentsCard'
import { AssetBenchmarkCard } from './benchmark/AssetBenchmarkCard'
import { EngagementScore } from './reports/EngagementScore'
import { RelationsPanel } from './asset-relations/RelationsPanel'
import { NetworksSection } from './asset-relations/NetworksSection'
import { EntityTagsSection } from './tags/EntityTagsSection'
import { EntityIssuesList } from './issues/EntityIssuesList'
import { AssetLabelsButton } from './labels/AssetLabelsButton'
import { AppIcon } from './ui/icon'
import { mdiArchiveArrowDownOutline, mdiArchiveArrowUpOutline, mdiCameraOutline, mdiCheck, mdiCheckDecagram, mdiCheckboxBlankOutline, mdiCheckboxMarkedOutline, mdiClose, mdiDomain, mdiFileTreeOutline, mdiFingerprint, mdiGauge, mdiGavel, mdiGraphOutline, mdiHomeAccount, mdiIdentifier, mdiMap, mdiMapMarkerRadius, mdiPlayCircleOutline, mdiScaleBalance, mdiStar, mdiStarOutline, mdiTicketOutline, mdiVectorSquare, mdiWrench  } from '@mdi/js'
import { useFavoriteAssets } from '../hooks/useFavoriteAssets'
import { SECTION_ICONS } from '../lib/section-icons'


// Unicode glyphs reused in <option> labels where JSX icons can't be rendered.
const NEUTRAL_BOOLEAN_TRUE_GLYPH = '☑'
const NEUTRAL_BOOLEAN_FALSE_GLYPH = '☐'

// Grouped intention headers over the asset-card tabs
// (spec: "Grouped Tab Headers on the Building Card").
// Single source of truth for tab ORDER and GROUPING. Grouping is expressed as a
// visible label only — never as a navigation level. Tab ids here are the ids of
// the existing `tabs` entries; a group only renders the tabs that are actually
// visible for the current asset type. Fiabilisation now lives strictly in the
// onboarding wizard (spec 2026-07-09-fiabilisation-onboarding-only), so
// `data-reliability` is no longer an asset-page tab.
type TabGroup = { id: string; labelKey: string; tabs: string[] }
const ASSET_TAB_GROUPS: TabGroup[] = [
    { id: 'connaitre', labelKey: 'detail.tabgroup_connaitre', tabs: ['photo', 'plan', 'composite-plan', 'site-map', 'cadastre', 'equipment', 'data', 'properties'] },
    { id: 'piloter', labelKey: 'detail.tabgroup_piloter', tabs: ['compliance', 'occupation', 'contracts', 'rent-roll', 'decisions', 'capex', 'interventions'] },
    { id: 'tracer', labelKey: 'detail.tabgroup_tracer', tabs: ['documents', 'activity', 'issues'] },
]

const getBooleanDisplay = (value: boolean, semantic?: string, t?: (key: string) => string): ReactNode => {
    const trueLabel = t ? t('detail.boolean_true') : 'True'
    const falseLabel = t ? t('detail.boolean_false') : 'False'
    switch (semantic) {
        case 'positive':
            return value ? '✅' : '❌'
        case 'negative':
            return value ? '❌' : '✅'
        case 'personal':
            return value ? '👍' : '👎'
        case 'warning':
            return value ? '⚠️' : '✅'
        case 'neutral':
        default: {
            const label = value ? trueLabel : falseLabel
            const icon = value ? mdiCheckboxMarkedOutline : mdiCheckboxBlankOutline
            const tone = value ? 'text-primary' : 'text-muted-foreground'
            return (
                <span
                    data-testid="boolean-value-neutral"
                    data-boolean-value={value ? 'true' : 'false'}
                    title={label}
                    aria-label={label}
                    role="img"
                    className="inline-flex items-center"
                >
                    <AppIcon path={icon} size="md" className={tone} aria-hidden="true" />
                </span>
            )
        }
    }
}

const getBooleanOptions = (semantic?: string, t?: (key: string) => string) => {
    const trueLabel = t ? t('detail.boolean_true') : 'True'
    const falseLabel = t ? t('detail.boolean_false') : 'False'
    switch (semantic) {
        case 'positive':
            return [{ value: 'true', label: `${trueLabel} (✅)` }, { value: 'false', label: `${falseLabel} (❌)` }]
        case 'negative':
            return [{ value: 'true', label: `${trueLabel} (❌)` }, { value: 'false', label: `${falseLabel} (✅)` }]
        case 'personal':
            return [{ value: 'true', label: `${trueLabel} (👍)` }, { value: 'false', label: `${falseLabel} (👎)` }]
        case 'warning':
            return [{ value: 'true', label: `${trueLabel} (⚠️)` }, { value: 'false', label: `${falseLabel} (✅)` }]
        case 'neutral':
        default:
            return [
                { value: 'true', label: `${NEUTRAL_BOOLEAN_TRUE_GLYPH} ${trueLabel}` },
                { value: 'false', label: `${NEUTRAL_BOOLEAN_FALSE_GLYPH} ${falseLabel}` },
            ]
    }
}


// Enum property input components
function EnumPropertyInput({ propertyDefinitionUid, value, onChange, org }: { propertyDefinitionUid: string, value: string | string[], onChange: (value: string | string[]) => void, org: string }) {
    const { t } = useTranslation('assets')
    const { data: enumValues = [], isLoading: enumValuesLoading } = usePropertyEnumValues(org, propertyDefinitionUid)
    const { data: propertyDefinition, isLoading: propertyDefLoading } = usePropertyDefinition(org, propertyDefinitionUid)

    const getEnumValueDisplayName = (enumValue: PropertyEnumValue) => {
        if (typeof enumValue.name === 'object' && enumValue.name !== null) {
            return enumValue.name.en || enumValue.name.fr || Object.values(enumValue.name)[0] || enumValue.value
        }
        return enumValue.value
    }

    if (enumValuesLoading || propertyDefLoading) {
        return (
            <input
                type="text"
                value={Array.isArray(value) ? value.join(', ') : value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded shadow-sm focus:outline-none focus:ring-ring focus:border-ring text-sm bg-background text-foreground"
                placeholder={t('detail.loading_enum_values')}
                disabled
            />
        )
    }

    const isMultiple = propertyDefinition?.properties?.multiple === true

    if (isMultiple) {
        const selectedValues = Array.isArray(value) ? value : (value ? value.split(',').map(v => v.trim()).filter(v => v) : [])

        // Find invalid values (selected but not in current enum options)
        const validEnumValueSet = new Set(enumValues.map(ev => ev.value))
        const invalidValues = selectedValues.filter(val => !validEnumValueSet.has(val))

        return (
            <div className="space-y-2">
                {/* Show invalid values separately with warning styling */}
                {invalidValues.length > 0 && (
                    <div className="border-l-4 border-orange-400 pl-3 bg-orange-50 dark:bg-orange-900/20 py-2 rounded-r">
                        <p className="text-sm text-orange-700 dark:text-orange-300 mb-2 font-medium">
                            {t('detail.invalid_values_title')}
                        </p>
                        {invalidValues.map((invalidValue) => (
                            <label key={`invalid-${invalidValue}`} className="flex items-center space-x-2 cursor-pointer p-2 rounded border hover:bg-orange-100 dark:hover:bg-orange-900/30">
                                <input
                                    type="checkbox"
                                    checked={true}
                                    onChange={() => {
                                        const newSelectedValues = selectedValues.filter(v => v !== invalidValue)
                                        onChange(newSelectedValues)
                                    }}
                                    className="rounded border-orange-400 text-orange-600 focus:ring-orange-500"
                                />
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-orange-700 dark:text-orange-300 line-through">
                                        {invalidValue}
                                    </span>
                                    <span className="text-sm text-orange-600 dark:text-orange-400">
                                        {t('detail.click_to_remove')}
                                    </span>
                                </div>
                            </label>
                        ))}
                    </div>
                )}

                {/* Show valid enum options */}
                {enumValues.map((enumValue) => {
                    const color = enumValue.properties?.color as string
                    const isSelected = selectedValues.includes(enumValue.value)

                    return (
                        <label key={enumValue.value} className="flex items-center space-x-2 cursor-pointer p-2 rounded border hover:bg-muted/50">
                            <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                    const newSelectedValues = e.target.checked
                                        ? [...selectedValues, enumValue.value]
                                        : selectedValues.filter(v => v !== enumValue.value)
                                    onChange(newSelectedValues)
                                }}
                                className="rounded border-border text-primary focus:ring-primary"
                            />
                            <div className="flex items-center gap-2">
                                {color && (
                                    <div
                                        className="inline-block w-4 h-4 rounded border border-gray-300"
                                        style={{ backgroundColor: color }}
                                        title={`Color: ${color}`}
                                    />
                                )}
                                <span className="text-sm">{getEnumValueDisplayName(enumValue)}</span>
                            </div>
                        </label>
                    )
                })}
            </div>
        )
    }

    return (
        <select
            value={Array.isArray(value) ? value[0] || '' : value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded shadow-sm focus:outline-none focus:ring-ring focus:border-ring text-sm bg-background text-foreground"
        >
            <option value="">{t('detail.select_value')}</option>
            {enumValues.map((enumValue) => {
                const color = enumValue.properties?.color as string
                return (
                    <option key={enumValue.value} value={enumValue.value}>
                        {color ? `● ` : ''}{getEnumValueDisplayName(enumValue)}
                    </option>
                )
            })}
        </select>
    )
}

function EnumPropertyEditInput({ propertyDefinitionUid, value, onChange, disabled, hasError, org }: { propertyDefinitionUid: string, value: string | string[], onChange: (value: string | string[]) => void, disabled: boolean, hasError: boolean, org: string }) {
    const { t } = useTranslation('assets')
    const { data: enumValues = [], isLoading: enumValuesLoading } = usePropertyEnumValues(org, propertyDefinitionUid)
    const { data: propertyDefinition, isLoading: propertyDefLoading } = usePropertyDefinition(org, propertyDefinitionUid)

    const getEnumValueDisplayName = (enumValue: PropertyEnumValue) => {
        if (typeof enumValue.name === 'object' && enumValue.name !== null) {
            return enumValue.name.en || enumValue.name.fr || Object.values(enumValue.name)[0] || enumValue.value
        }
        return enumValue.value
    }

    if (enumValuesLoading || propertyDefLoading) {
        return (
            <input
                type="text"
                value={Array.isArray(value) ? value.join(', ') : value}
                onChange={(e) => onChange(e.target.value)}
                className={`flex-1 px-2 py-1 border ${hasError ? 'border-destructive' : 'border-border'} rounded text-sm bg-background text-foreground`}
                placeholder={t('detail.loading_enum_values')}
                disabled={true}
                autoFocus
            />
        )
    }

    const isMultiple = propertyDefinition?.properties?.multiple === true

    if (isMultiple) {
        const selectedValues = Array.isArray(value) ? value : (value ? value.split(',').map(v => v.trim()).filter(v => v) : [])

        // Find invalid values (selected but not in current enum options)
        const validEnumValueSet = new Set(enumValues.map(ev => ev.value))
        const invalidValues = selectedValues.filter(val => !validEnumValueSet.has(val))

        return (
            <div className={`flex-1 space-y-1 ${hasError ? 'border-destructive' : ''}`}>
                {/* Show invalid values separately with warning styling */}
                {invalidValues.length > 0 && (
                    <div className="border-l-4 border-orange-400 pl-2 bg-orange-50 dark:bg-orange-900/20 py-1">
                        <p className="text-xs text-orange-700 dark:text-orange-300 mb-1 font-medium">
                            {t('detail.invalid_values_title')}
                        </p>
                        {invalidValues.map((invalidValue) => (
                            <label key={`invalid-${invalidValue}`} className="flex items-center space-x-2 cursor-pointer p-1 text-xs">
                                <input
                                    type="checkbox"
                                    checked={true}
                                    onChange={() => {
                                        const newSelectedValues = selectedValues.filter(v => v !== invalidValue)
                                        onChange(newSelectedValues)
                                    }}
                                    className="rounded border-orange-400 text-orange-600 focus:ring-orange-500"
                                    disabled={disabled}
                                />
                                <div className="flex items-center gap-1">
                                    <span className="text-xs text-orange-700 dark:text-orange-300 line-through">
                                        {invalidValue}
                                    </span>
                                    <span className="text-xs text-orange-600 dark:text-orange-400">
                                        {t('detail.click_to_remove')}
                                    </span>
                                </div>
                            </label>
                        ))}
                    </div>
                )}

                {/* Show valid enum options */}
                {enumValues.map((enumValue) => {
                    const color = enumValue.properties?.color as string
                    const isSelected = selectedValues.includes(enumValue.value)

                    return (
                        <label key={enumValue.value} className="flex items-center space-x-2 cursor-pointer p-1 text-xs">
                            <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                    const newSelectedValues = e.target.checked
                                        ? [...selectedValues, enumValue.value]
                                        : selectedValues.filter(v => v !== enumValue.value)
                                    onChange(newSelectedValues)
                                }}
                                className="rounded border-border text-primary focus:ring-primary"
                                disabled={disabled}
                            />
                            <div className="flex items-center gap-1">
                                {color && (
                                    <div
                                        className="inline-block w-3 h-3 rounded border border-gray-300"
                                        style={{ backgroundColor: color }}
                                        title={`Color: ${color}`}
                                    />
                                )}
                                <span className="text-xs">{getEnumValueDisplayName(enumValue)}</span>
                            </div>
                        </label>
                    )
                })}
            </div>
        )
    }

    return (
        <select
            value={Array.isArray(value) ? value[0] || '' : value}
            onChange={(e) => onChange(e.target.value)}
            className={`flex-1 px-2 py-1 border ${hasError ? 'border-destructive' : 'border-border'} rounded text-sm bg-background text-foreground`}
            disabled={disabled}
            autoFocus
        >
            <option value="">{t('detail.select_value')}</option>
            {enumValues.map((enumValue) => {
                const color = enumValue.properties?.color as string
                return (
                    <option key={enumValue.value} value={enumValue.value}>
                        {color ? `● ` : ''}{getEnumValueDisplayName(enumValue)}
                    </option>
                )
            })}
        </select>
    )
}

export function AssetDetailPage({ activeTab, setActiveTab }: { activeTab?: string, setActiveTab?: (tab: string) => void }) {
    const { t, i18n } = useTranslation('assets')
    const { t: tData } = useTranslation('data')
    const { org: orgParam, assetUid } = useParams({ from: '/$org/assets/$assetUid' })
    const org = orgParam || 'demo'
    // Structural actions on the asset (archive, restore, delete) are
    // `assets.write`. The inline editors below stay mounted but render
    // read-only instead of vanishing — the data must remain visible.
    const { t: tAssemblies } = useTranslation('assemblies')
    const { t: tUsers } = useTranslation('users')
    const { t: tLeases } = useTranslation('leases')
    const canWriteAssets = useCan('assets.write')
    const canReadKnowledge = useCan('knowledge.read')
    // The Access tab is for the people who manage access, not for the people
    // who hold it (spec 2026-09-04-03, frontend change 3).
    const canReadMembers = useCan('members.read')
    const canReadFinance = useCan('finance.read')
    const canReadIssues = useCan('issues.read')
    // The agent runs panel reads and launches /agent-runs, a data.write section (route-permissions.ts).
    const canUseAgentRuns = useCan('data.write')
    const { permissions: myPermissions, membershipKnown } = usePermissions()
    // A resident's asset page is the lighter one of their shell
    // (lib/sidebar-menus.ts, spec 2026-10-07-03): fewer tabs, no manager
    // furniture, breadcrumbs rooted at "My places". Decluttering only: the
    // API stays the enforcer.
    const memberKind = useMemberKind()
    const kindShell = shellForKind(memberKind)
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    const { data: assetData, isLoading: loading, error: queryError, refetch: refetchAsset } = useAsset(org, assetUid)
    const { data: properties = [] } = useAssetProperties(org, assetData?.uid)
    const { data: assetChainData } = useAssetWithParentChain(org, assetData?.uid)
    const { isFavorite, toggleFavorite } = useFavoriteAssets(org)

    // Live entity notifications for this specific asset — covers the detail
    // view, its properties tab, and its picture. The documents/contracts
    // tabs are covered by their own collection-scope subscriptions (see
    // AssetDocuments.tsx) since a document/contract hint carries the
    // DOCUMENT's or CONTRACT's uid, not this asset's.
    useLiveSubscription({ entity: 'assets', uid: assetUid })

    // Polling safety net: useAsset itself has no refetchInterval (a plain
    // 5-min-staleTime query), so live updates are the ONLY freshness
    // mechanism for this page unless we also poll here. isScopeLive tracks
    // whether the live subscription above is actually connected+acked —
    // while it is, dynamicRefresh pins to a lazy 5-min interval (redundant
    // with live updates, just a backstop); the moment the socket drops,
    // dynamicRefresh's adaptive backoff takes over as the real freshness
    // mechanism again. See docs/frontend/dynamic-refresh.md.
    const isAssetScopeLive = useScopeLive({ entity: 'assets', uid: assetUid })
    const assetDynamicRefresh = useDynamicRefresh({
        enabled: !!assetUid,
        defaultInterval: 3000,
        live: isAssetScopeLive,
    })
    useEffect(() => {
        if (assetDynamicRefresh.timeRemaining !== 0) return
        void refetchAsset()
    }, [assetDynamicRefresh.timeRemaining, refetchAsset])

    // Handle tab change with URL update
    const handleTabChange = (tabId: string) => {
        const url = new URL(window.location.href)
        url.searchParams.set('tab', tabId)
        window.history.pushState({}, '', url.toString())
        setActiveTab?.(tabId)
    }

    // Fetch specific parent asset for efficient display
    const { data: parentAsset } = useAsset(
        org,
        assetData?.parent_asset_uid || ''
    )

    const { data: assetTypes = [] } = useAssetTypes(org)

    // Define tabs — plan tab is the spatial overview (StackingPlan for buildings,
    // AssetPlanViewer for floor/unit/room).
    const currentAssetType = assetTypes.find(at => at.uid === assetData?.asset_type_uid)
    const assetTypeCode = currentAssetType?.code
    const isCapexPlannable = Boolean(currentAssetType?.properties?.capex_plannable)
    const isCapexWorksite = Boolean(currentAssetType?.properties?.capex_worksite)
    // Only asset types explicitly flagged `with_address` (building, house,
    // parcel_land) render the address display/editor — equipment, floors,
    // units, etc. don't have their own postal address.
    const hasAddressFlag = Boolean(currentAssetType?.properties?.with_address)

    // The asset type's associated properties (inherited: an equipment leaf like
    // `roof_pitched` gets the `equipment` base set — État, année de mise en
    // service…). Passed as baseCodes to the Properties tab so mandatory-but-empty
    // fields show as fillable ghost rows; without this the capex engine has no
    // condition to work from. Mirrors the Data tab (AssetDataTab).
    const { data: assocTypeProps = [] } = useAssetTypeProperties(org, currentAssetType?.uid || '', true)
    const propertiesTabBaseCodes = useMemo(
        () => assocTypeProps.map((p: { code?: string }) => p.code).filter((c): c is string => Boolean(c)),
        [assocTypeProps],
    )

    // True for equipment and building elements — anything whose type descends
    // from `equipment` (hvac_boiler → trade_hvac → equipment). The Photo tab
    // hangs off this, and only this: it is the surface a QR sticker points at,
    // and stickers are glued on things, not on floors or buildings.
    //
    // The quantity walk that used to sit beside this one is gone: the quantity
    // moved into the property registry, and its hook does its own lookup.
    const isEquipmentAsset = assetTypeDescendsFrom(assetTypes, assetData?.asset_type_uid, 'equipment')
    const isBuildingAsset = assetTypeDescendsFrom(assetTypes, assetData?.asset_type_uid, 'building')
    // A meter: its supply contract card (spec 2026-10-27-16).
    const isMeterAsset = assetTypeDescendsFrom(assetTypes, assetData?.asset_type_uid, 'trade_metering')
    // The contract checklist of the Contracts tab (spec 2026-10-11-17): a site,
    // or a building (house included).
    const contractChecklist: 'site' | 'building' | undefined =
        assetTypeCode === 'building_group' ? 'site' : isBuildingAsset ? 'building' : undefined

    // On a site (building_group) the geographic "Plan de masse" tab replaces the
    // floor-plan viewer, so `plan` is dropped there (see siteMapTabApplies below).
    //
    // The list is the LOCATION types that may own plan geometry — the front's
    // half of `assets.VectorGeometryTypeCodes` minus `building_group` — and
    // `room` is resolved transitively rather than listed (spec 2026-09-19-02):
    // the old literal set silently dropped `common_area`, which outlines itself
    // exactly as the unit beside it does, and every `room_*` sub-type the
    // ontology says inherits from `room`.
    const planTabTypeApplies = ['building', 'floor', 'unit', 'common_area'].includes(lotBaseTypeCode(assetTypeCode))
        || assetTypeCodeDescendsFrom(assetTypes, assetTypeCode, 'room')
    // ...but a common area is the one of those types that can neither upload a
    // scan of its own (it is absent from the backend's `planAssetTypes`) nor
    // open the plan editor (`PLAN_EDITABLE_TYPE_CODES`) — its outline is drawn
    // from its FLOOR's editor. So an empty Plan tab on one is a dead end: a drop
    // zone whose upload the API refuses. It is offered only once there is
    // something to show — its own geometry, or the ancestor frame it borrows —
    // which is the page-level form of spec 2026-09-18-06's "nothing of its own"
    // rule. Every other type keeps the unconditional tab, because for them the
    // empty tab IS the way a plan gets added.
    const planTabNeedsContent = assetTypeCode === 'common_area'
    const planContentQueryEnabled = planTabNeedsContent && !!assetData?.uid
    // Both queries are keyed exactly as `AssetPlanViewer` keys them, so opening
    // the tab reuses these responses instead of fetching them again.
    const planOwnRepsQuery = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/assets/{assetUid}/representations',
        { params: { path: { orgCode: org, assetUid: assetData?.uid ?? '' } } },
        { enabled: planContentQueryEnabled },
    )
    const planSceneQuery = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/assets/{assetUid}/plan/scene',
        { params: { path: { orgCode: org, assetUid: assetData?.uid ?? '' }, query: { context: 'asset' as const } } },
        { enabled: planContentQueryEnabled },
    )
    const planTabApplies = planTabTypeApplies
        && (!planTabNeedsContent
            || (planOwnRepsQuery.data?.representations?.length ?? 0) > 0
            || (planSceneQuery.data?.entries?.length ?? 0) > 0)
    // Equipment tab visible on every location type that the ontology lets
    // equipment hang from — Site / Building Group / Building / Floor / Unit
    // (spec 2026-05-02-01-equipment-tab §2.1) plus Common area and Room.
    //
    // The spec excluded Room on the grounds that the right panel's `Children`
    // section already lists directly-attached equipment. It does not: that
    // section queries `?in_tree=true` (useChildAssets) and every equipment leaf
    // type is `in_tree: false`, so it renders nothing and hides itself. Common
    // area was never in the spec's table at all, yet 7 of the 11 trades declare
    // `common_area` among their possible parents (and `room` accepts
    // trade_finishes) — a chaufferie or a local technique is precisely where
    // collective equipment lives. Hidden on Equipment itself (leaf).
    // `outdoor` joins the list for the same reason (spec 2026-09-13-05): the
    // trades that carry outdoor equipment (site, elec, plumbing, fire, hvac,
    // furniture) all accept it as a parent, so its own page needs the same
    // Add-equipment entry point a site or a building gets.
    const equipmentTabApplies = ['parcel_land', 'building_group', 'building', 'floor', 'unit', 'common_area', 'room', 'outdoor'].includes(lotBaseTypeCode(assetTypeCode))
    // Interventions (carnet d'entretien) apply to buildings and their sub-assets.
    // An equipment lists its own interventions and its components' (spec
    // 2026-10-27-17): the subtree filter follows the `part_of` ancestry.
    const interventionsTabApplies = ['building_group', 'building', 'floor', 'unit'].includes(lotBaseTypeCode(assetTypeCode)) || isEquipmentAsset
    // Site Map (Carte) tab — geographic footprint viewer, only on the "site"
    // (building_group / parcel_land). Distinct from the floor-plan "Plan" tab.
    // Spec 2026-06-20-02-site-map-tab-frontend §9.
    // Site-map is a building_group concern (buildings + footprints within a site).
    // Parcels get the dedicated Cadastre tab instead, so site-map is dropped there.
    const siteMapTabApplies = assetTypeCode === 'building_group'
    // Composite plan (shared referential) — site (`building_group`) frames only:
    // on a building it merely lays its floors side by side, and each floor's own
    // Plan tab already shows the useful view. The `composite_plan` flag is still
    // computed on buildings (the site's flag reads its nested buildings).
    // Spec 2026-10-28-01-composite-plan-tab-off-buildings. Also,
    // AND only once the frame actually has something registered (the
    // `composite_plan` asset flag, maintained by frames.Service on every
    // registration write — internal/handlers/frames/asset_flags.go).
    // Distinct from the `plan` tab: it stitches every registered plan of the
    // frame (and of one level of child frames) into a single SVG.
    // Spec 2026-07-27-08-vector-geometry-common-referential §6.
    // Spec 2026-08-01-01-composite-plan-tab-conditional-visibility.
    const compositePlanTabApplies = assetTypeCode === 'building_group'
        && (assetData?.flags?.includes('composite_plan') ?? false)
    // Cadastre tab — parcel-only: cadastral parcel map + risk zoning auto-filled
    // from the parcel's (or its parent site's) address (BAN → IGN → Géorisques).
    const cadastreTabApplies = assetTypeCode === 'parcel_land'

    // Engagement (completeness score + deadline calendar) — a building-level
    // dossier metric. Spec 2026-06-20-08 §4/§5.
    //
    // Spec 2026-08-26-01 moves this into an org-level Reports section, of which
    // it becomes the first screen. That section does not exist yet, so the tab
    // stays until the spec lands: a link into a route the router does not know
    // is a dead button, not a migration.
    const engagementTabApplies = ['building_group', 'building'].includes(assetTypeCode || '')
    // Data tab — the asset's property registry (base properties, values, source
    // and history), coherent for site / building / lot. Replaces both the
    // fiabilisation tab and the flat Properties tab for those types.
    // (spec 2026-07-03-data-tab-per-asset-type)
    const dataTabApplies = DATA_TAB_ASSET_TYPES.includes(lotBaseTypeCode(assetTypeCode))
    // A room inside a lot can be let on its own (spec 2026-10-25-06 step 8).
    const parentTypeCode = assetTypes.find(at => at.uid === (parentAsset as { asset_type_uid?: string } | undefined)?.asset_type_uid)?.code
    const isRoomInLot = (assetTypeCode === 'room' || (assetTypeCode ?? '').startsWith('room_')) && lotBaseTypeCode(parentTypeCode) === 'unit'
    const occupationTabApplies = ['unit', 'building', 'building_group'].includes(lotBaseTypeCode(assetTypeCode)) || isRoomInLot

    // Core asset columns for the Properties tab. Types with a Data tab get them
    // from AssetDataTab, which calls the same hook.
    const { coreRows, onSaveCoreRow } = useAssetCoreRows(org, assetData?.uid || '', assetData?.asset_type_uid)

    // Spec 2026-05-06-02 §5 — categories that the Properties tab keeps when
    // a context tab takes over for them. Routing:
    //   - surfaces, occupation → Plan tab when applicable, else Properties
    //   - condition, energy, technical → Equipment tab when applicable, else Properties
    //   - compliance → Compliance tab (always)
    //   - identity, location, financial, general, steering → Properties (always)
    const propertiesTabCategories = (() => {
        const all = ['identity', 'location', 'financial', 'general', 'surfaces', 'occupation', 'condition', 'energy', 'technical', 'compliance', 'steering']
        const removed = new Set<string>([
            ...(planTabApplies ? ['surfaces', 'occupation'] : []),
            ...(equipmentTabApplies ? ['condition', 'energy', 'technical'] : []),
            'compliance', // always taken over by the Compliance tab
        ])
        return all.filter((c) => !removed.has(c))
    })()
    // Each tab declares the permission the API will demand of its content
    // (spec 2026-09-06-04, decision 7). A tab the caller cannot load is not
    // offered: a scoped member who lacks `contracts.read` sees no Contracts
    // tab instead of a tab that answers 403. Undefined = no permission needed.
    const tabs: Array<{ id: string, label: string, icon?: string, description?: string, mobileOnly?: boolean, badge?: number, permission?: Permission }> = [
        ...(isEquipmentAsset ? [{ id: 'photo', label: t('detail.tab_photo'), icon: mdiCameraOutline, description: t('detail.tab_photo_description') }] : []),
        ...(planTabApplies ? [{ id: 'plan', label: t('detail.tab_plan'), icon: mdiDomain, description: t('detail.tab_plan_description') }] : []),
        ...(compositePlanTabApplies ? [{ id: 'composite-plan', label: t('detail.tab_composite_plan'), icon: mdiVectorSquare, description: t('detail.tab_composite_plan_description') }] : []),
        ...(siteMapTabApplies ? [{ id: 'site-map', label: t('siteMap:tab.label'), icon: mdiMap, description: t('siteMap:tab.description') }] : []),
        ...(cadastreTabApplies ? [{ id: 'cadastre', label: t('cadastre:tab.label'), icon: mdiMapMarkerRadius, description: t('cadastre:tab.description') }] : []),
        { id: 'compliance', label: t('detail.tab_compliance'), icon: mdiScaleBalance, description: t('detail.tab_compliance_description'), permission: 'compliance.read' as Permission },
        ...(engagementTabApplies ? [{ id: 'engagement', label: t('engagement:tab.label'), icon: mdiGauge, description: t('engagement:tab.description') }] : []),
        { id: 'documents', label: t('detail.tab_documents'), icon: SECTION_ICONS.documents, description: t('detail.tab_documents_description'), permission: 'documents.read' as Permission },
        { id: 'contracts', label: t('detail.tab_contracts'), icon: SECTION_ICONS.contracts, description: t('detail.tab_contracts_description'), permission: 'contracts.read' as Permission },
        // Occupation: the lettability gate of a lot, or of the lots under a
        // building or site (spec 2026-10-25-04 §12). Its habitability calls are
        // `assets`; the lease and rent parts hide themselves without contracts.read.
        ...(occupationTabApplies ? [{ id: 'occupation', label: tLeases('occupation.tab'), icon: mdiHomeAccount, description: tLeases('occupation.tab_description') }] : []),
        // The rent roll of a building, a site or an organizational asset
        // (spec 2026-10-25-03 §4, 2026-10-27-15 §3).
        ...(['building', 'building_group', 'portfolio', 'agency', 'entity'].includes(assetTypeCode ?? '') ? [{ id: 'rent-roll', label: tLeases('rent_roll.tab'), icon: SECTION_ICONS.leases, description: tLeases('rent_roll.tab_description'), permission: 'contracts.read' as Permission }] : []),
        // Was it voted? The assembly decisions that concern this asset or the
        // building above it (spec 2026-10-10-08). Not on equipment.
        ...(!isEquipmentAsset ? [{ id: 'decisions', label: tAssemblies('decisions.title'), icon: mdiGavel, description: tAssemblies('decisions.description'), permission: 'contracts.read' as Permission }] : []),
        ...((isCapexPlannable || isCapexWorksite) ? [{ id: 'capex', label: t('detail.tab_capex'), icon: SECTION_ICONS.capex, description: t('detail.tab_capex_description'), permission: 'capex.read' as Permission }] : []),
        ...(equipmentTabApplies ? [{ id: 'equipment', label: t('detail.tab_equipment'), icon: mdiWrench, description: t('detail.tab_equipment_description') }] : []),
        ...(interventionsTabApplies ? [{ id: 'interventions', label: t('detail.tab_interventions', { defaultValue: 'Interventions' }), icon: SECTION_ICONS.interventions, description: t('detail.tab_interventions_description', { defaultValue: "Carnet d'entretien : préventif, correctif, travaux" }), permission: 'interventions.read' as Permission }] : []),
        ...(dataTabApplies ? [{ id: 'data', label: tData('asset.tab_label'), icon: mdiCheckDecagram, description: tData('asset.tab_description') }] : []),
        { id: 'activity', label: t('detail.tab_activity'), icon: SECTION_ICONS.activity },
        { id: 'issues', label: t('detail.tab_issues'), icon: mdiTicketOutline, description: t('detail.tab_issues_description'), permission: 'issues.read' as Permission },
        // On site / building / lot the Properties surface is absorbed by the
        // coherent Data tab, so the standalone Properties tab is dropped there.
        ...(!dataTabApplies ? [{ id: 'properties', label: t('detail.tab_properties'), icon: mdiFingerprint, description: t('detail.tab_properties_description') }] : []),
        // The right rail (Contacts / Children / Notes) and its mobile tab are
        // gone, and Access became a header button (spec 2026-10-25-20):
        // contacts are the Intervenants of the Contracts tab, notes sit on
        // Activity, children on the registry tab.
    ]
    // Desktop tab bar grouped into intention clusters (Connaître · Piloter ·
    // Tracer). Grouping only reorders + labels; each group renders the subset of
    // its tabs that is actually visible for this asset type. Any visible tab not
    // covered by a group is appended (kept ungrouped) so nothing is ever dropped.
    // Drop the tabs whose permission the caller does not hold, before anything
    // else looks at the list: the tab strip, the default-tab pick and the
    // URL clamp must all agree on the same set (spec 2026-09-06-04). The
    // payload's `permissions` is already the stripped union of the membership
    // role and every active grant, so no extra request is needed. While the
    // membership is still unknown nothing is dropped — the sidebar's optimism
    // applies here too, and the API stays the enforcer.
    const allowedTabs = useMemo(
        () => tabs.filter((tab) =>
            (!tab.permission || !membershipKnown || holdsPermission(myPermissions, tab.permission))
            && assetTabAllowedForKind(memberKind, tab.id)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [tabs.map((tab) => `${tab.id}:${tab.permission ?? ''}:${tab.badge ?? ''}`).join('|'), membershipKnown, myPermissions, memberKind],
    )
    const visibleTabs = allowedTabs.filter((tab) => !tab.mobileOnly)
    const tabsById = useMemo(() => new Map(visibleTabs.map((tab) => [tab.id, tab])), [visibleTabs])
    const tabGroups = useMemo(() => ASSET_TAB_GROUPS
        .map((group) => ({
            ...group,
            items: group.tabs.map((id) => tabsById.get(id)).filter((tab): tab is typeof visibleTabs[number] => Boolean(tab)),
        }))
        .filter((group) => group.items.length > 0),
        [tabsById])
    const groupedTabIds = useMemo(() => new Set(ASSET_TAB_GROUPS.flatMap((group) => group.tabs)), [])
    const ungroupedTabs = visibleTabs.filter((tab) => !groupedTabIds.has(tab.id))
    // Flattened display order across group boundaries — drives keyboard
    // arrow navigation and the default-tab pick.
    const orderedVisibleTabs = useMemo(
        () => [...tabGroups.flatMap((group) => group.items), ...ungroupedTabs],
        [tabGroups, ungroupedTabs],
    )
    // Global default: Plan when present (§6), then Site map on a site
    // (spec 2026-06-20-02-site-map-tab-frontend §9), else the first tab in
    // display order.
    //
    // The explicit site-map step matters: `composite-plan` was later inserted
    // ahead of it in the `connaitre` group, so a positional fallback alone
    // silently made the composite plan the landing tab of every site — a
    // default nobody asked for (that spec only asked for the tab to exist).
    // `photo` leads on equipment: a QR scan lands on the asset page with no
    // ?tab, and what the scanner wants is the photo, not the compliance grid.
    const preferredDefaultTabIds = kindShell.assetDefaultTabs ?? ['photo', 'plan', 'site-map']
    const defaultTabId = preferredDefaultTabIds
        .map((id) => orderedVisibleTabs.find((tab) => tab.id === id)?.id)
        .find(Boolean)
        || orderedVisibleTabs[0]?.id
        || 'properties'

    // Clamp the requested tab to the ones that actually apply to this asset.
    // `activeTab` is initialised from the URL and is NOT reset when navigating
    // between assets (same route, param change → no remount) nor on browser
    // back/forward, so a tab picked on one asset (e.g. "plan" on a building)
    // can linger onto an asset that has no such tab (e.g. a site) and render
    // orphan content — the "Déposez un plan" dropzone with no matching tab in
    // the strip (issue #1017). Falling back to the default tab keeps the shown
    // content consistent with the visible tab strip for every asset type.
    // A URL pointing at a tab the caller may not see falls back to the first
    // visible one, exactly like a URL pointing at a tab this asset type has
    // no business showing.
    const validTabIds = new Set(allowedTabs.map((tab) => tab.id))
    // On site / building / lot the Data tab absorbs the Properties surface, so
    // there is no standalone `properties` tab — but deep-links into a property
    // (the activity feed links `?tab=properties&propertyHistory=…`) still ask
    // for it. Resolve those onto Data, which renders the same AssetProperties
    // and honours the drawer, instead of dropping to the default tab.
    const requestedTab = activeTab === 'properties' && !validTabIds.has('properties') && validTabIds.has('data')
        ? 'data'
        : activeTab
    const effectiveTab = requestedTab && validTabIds.has(requestedTab) ? requestedTab : defaultTabId

    // Roving focus for keyboard arrow navigation across the flattened sequence.
    const tabRefs = useRef<Array<HTMLButtonElement | null>>([])
    const handleTabKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
        const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End']
        if (!keys.includes(e.key)) return
        e.preventDefault()
        const count = orderedVisibleTabs.length
        if (count === 0) return
        let nextIndex = index
        if (e.key === 'ArrowRight') nextIndex = (index + 1) % count
        else if (e.key === 'ArrowLeft') nextIndex = (index - 1 + count) % count
        else if (e.key === 'Home') nextIndex = 0
        else if (e.key === 'End') nextIndex = count - 1
        const nextTab = orderedVisibleTabs[nextIndex]
        if (!nextTab) return
        handleTabChange(nextTab.id)
        tabRefs.current[nextIndex]?.focus()
    }
    const renderTabButton = (tab: typeof visibleTabs[number]) => {
        const globalIndex = orderedVisibleTabs.indexOf(tab)
        const isActive = effectiveTab === tab.id
        return (
            <button
                key={tab.id}
                ref={(el) => { tabRefs.current[globalIndex] = el }}
                role="tab"
                aria-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                onClick={() => handleTabChange(tab.id)}
                onKeyDown={(e) => handleTabKeyDown(e, globalIndex)}
                className={`
                    pb-3 pt-1 border-b-2 text-sm transition-colors whitespace-nowrap flex-shrink-0
                    ${isActive
                        ? 'border-primary text-foreground font-medium'
                        : 'border-transparent text-muted-foreground hover:text-foreground font-normal'
                    }
                `}
                data-testid={`tab-${tab.id}`}
            >
                {tab.label}
                {tab.badge != null && (
                    <span
                        className="ml-1.5 inline-flex min-w-4 items-center justify-center rounded-full bg-warning/15 px-1.5 text-[11px] font-bold text-warning"
                        data-testid={`tab-badge-${tab.id}`}
                    >
                        {tab.badge}
                    </span>
                )}
            </button>
        )
    }


    // Collect all property UIDs from asset properties for batch loading
    const propertyUids = useMemo(() => {
        const uids = properties
            .map(prop => prop.property_uid)
            .filter((uid): uid is string => Boolean(uid))
        return Array.from(new Set(uids)) // Remove duplicates
    }, [properties])

    // Batch load property definitions for the specific properties used by this asset
    const { data: propertyDefinitions = [] } = usePropertyDefinitionsByUids(org, propertyUids)

    // Property definitions for recent properties (sorted by updated_at, then by name on frontend)
    const { data: recentPropertyDefinitions = [] } = usePropertyDefinitions(org, {
        sort: '-updated_at',
        asset_uid: assetData?.uid
    })
    const updateAssetMutation = useUpdateAsset(org, assetData?.uid)
    const restoreAssetMutation = useRestoreAsset(org)
    const setPropertyValueMutation = useSetAssetPropertyValue(org, assetData?.uid)
    const updatePropertyValueMutation = useUpdateAssetPropertyValue(org, assetData?.uid)
    const deletePropertyValueMutation = useDeleteAssetPropertyValue(org, assetData?.uid)


    // API search handlers for ApiSearchableSelect
    const { request } = useApi()

    // Helper function to get property definition names
    const getPropertyDefinitionName = useCallback((uid: string | undefined) => {
        if (!uid) return t('detail.unknown_property')

        // First look in current asset's property definitions
        let propDef = propertyDefinitions.find(pd => pd.uid === uid)

        // If not found, look in recent property definitions
        if (!propDef) {
            propDef = recentPropertyDefinitions.find(pd => pd.uid === uid)
        }

        if (!propDef) return uid

        // Handle multilingual names
        if (typeof propDef.name === 'object' && propDef.name !== null) {
            const names = propDef.name as Record<string, string>
            const locale = i18n.language
            return names[locale] || names.en || names.fr || Object.values(names)[0] || propDef.code || uid
        }

        return propDef.name || propDef.code || uid
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propertyDefinitions, recentPropertyDefinitions, i18n.language])

    const _searchPropertyDefinitions = useCallback(async (query: string): Promise<ApiSearchableSelectOption[]> => {
        try {
            // Use API search with query parameter
            const response = await request('/api/v1/organizations/{orgCode}/properties', 'GET', {
                params: {
                    path: { orgCode: org },
                    query: {
                        q: query.trim(),
                        sort: '-updated_at',
                        asset_uid: assetData?.uid
                    }
                }
            })

            const searchResults = ((response as { properties?: PropertyDefinition[] })?.properties || [])
            const locale = i18n.language
            return searchResults.map((propDef: any) => {
                // Extract name directly from the search result instead of using getPropertyDefinitionName
                let name: string
                if (typeof propDef.name === 'object' && propDef.name !== null) {
                    const names = propDef.name as Record<string, string>
                    name = names[locale] || names.en || names.fr || Object.values(names)[0] || propDef.code || propDef.uid
                } else {
                    name = propDef.name || propDef.code || propDef.uid
                }

                // Build label with flags
                const flags = []
                if (propDef.data_type === 'enum' && propDef.properties?.multiple === true) {
                    flags.push(t('detail.flag_multiple'))
                }
                if ((propDef.data_type === 'boolean' || propDef.data_type === 'enum') && propDef.flags?.includes('attribute')) {
                    flags.push(t('detail.flag_attribute'))
                }
                const flagsStr = flags.length > 0 ? ` [${flags.join(', ')}]` : ''

                return {
                    value: propDef.uid,
                    label: `${name} (${propDef.data_type})${flagsStr}`
                }
            })
        } catch (error) {
            console.error('Property definition search error:', error)
            return []
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [org, request, getPropertyDefinitionName, assetData?.uid, assetUid])

    const _handlePropertyDefinitionSelect = useCallback((option: { value: string; label: string }) => {
        setNewPropertyDefinitionUid(option.value)
        setNewPropertyDefinitionName(option.label)
        // Clear the property value when changing property definition
        setNewPropertyValue('')
    }, [])

    const [addressEditorOpen, setAddressEditorOpen] = useState(false)
    // The Access tab's "Invite" button (spec 2026-09-05-03) opens the shared
    // invite dialog pre-scoped to this asset instead of being a no-op.
    // The header « … » menu opens one of these dialogs at a time.
    const [headerDialog, setHeaderDialog] = useState<'move' | 'archive' | null>(null)
    const [editingNameField, setEditingNameField] = useState(false)
    // Parent: the header's inline editor is gone for good. Moving an asset is
    // the Move dialog below (spec 2026-09-18-01) and the property registry's
    // identity row, which share one save path; the field helpers no longer
    // carry a `parent_asset_uid` case at all.
    // The header no longer edits the external code — that moved to the Data tab's
    // Identité section (CoreRow). The state is retained write-only so the shared
    // field helpers (handleSaveField / startEditing / handleCancelEdit) keep
    // covering 'external_code' for their other callers.
    const [_editingExternalCodeField, setEditingExternalCodeField] = useState(false)
    const [editingName, setEditingName] = useState('')
    const [_editingExternalCode, setEditingExternalCode] = useState('')
    const [_editingQuantityField, setEditingQuantityField] = useState(false)
    const [_editingQuantity, setEditingQuantity] = useState('')

    // Add error states for each field
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

    // New property form state
    const [_showAddPropertyForm, setShowAddPropertyForm] = useState(false)
    const [newPropertyDefinitionUid, setNewPropertyDefinitionUid] = useState('')
    const [_newPropertyDefinitionName, setNewPropertyDefinitionName] = useState('')
    const [newPropertyValue, setNewPropertyValue] = useState<string | string[]>('')
    const [_addPropertyError, setAddPropertyError] = useState('')

    // Property editing state
    const [_editingPropertyUid, setEditingPropertyUid] = useState<string | null>(null)
    const [_editingPropertyValue, setEditingPropertyValue] = useState<string>('')
    const [propertyEditErrors, setPropertyEditErrors] = useState<Record<string, string>>({})


    const error = queryError?.message || null




    // Initialize editing values when asset data loads
    useEffect(() => {
        if (assetData) {
            setEditingName(assetData.name || '')
            setEditingExternalCode(assetData.external_ids?.code || '')
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assetData?.uid]) // Only depend on asset UID to avoid resetting editing values during updates


    // Helper function to get asset title
    const getAssetTitle = (asset: Record<string, unknown>) => {
        if (asset.name) {
            return asset.name as string
        }
        if (asset.external_ids && typeof asset.external_ids === 'object' && asset.external_ids !== null) {
            const externalIds = asset.external_ids as Record<string, unknown>
            if (externalIds.code) {
                return externalIds.code as string
            }
        }
        return asset.uid as string
    }

    // Helper function to get parent asset hierarchy for breadcrumbs using asset chain data
    const getParentHierarchy = () => {
        if (!assetChainData || assetChainData.length <= 1) {
            return []
        }

        // Filter out the current asset and create breadcrumb items for all parents
        return assetChainData
            .filter((asset: any) => asset.uid !== assetData?.uid)
            .map((parentAsset: any) => ({
                title: parentAsset.name || parentAsset.external_ids?.code || parentAsset.uid,
                path: `/${org}/assets/${parentAsset.uid}`,
                archivedAt: parentAsset.archived_at ?? null
            }))
    }

    // Set breadcrumbs when asset data is available
    useBreadcrumbs(
        assetData ? [
            ...(kindShell.myPlacesBreadcrumb
                ? [{ title: t('common:nav.my_places'), path: `/${org}` }]
                : [
                    { title: t('common:nav.home'), path: `/${org}` },
                    { title: t('common:nav.assets'), path: `/${org}/assets` },
                ]),
            ...getParentHierarchy(),
            { title: getAssetTitle(assetData), archivedAt: assetData.archived_at ?? null }
        ] : []
    )



    const _getPropertyDisplayValue = (value: unknown, propertyUid?: string) => {
        if (value === null || value === undefined) return 'N/A'
        if (Array.isArray(value)) {
            // For enum properties, this will be handled by EnumPropertyDisplay component
            // For other array types, just join with commas
            return value.join(', ')
        }
        if (typeof value === 'object') return JSON.stringify(value)

        // Format values based on property definition
        if (propertyUid) {
            const propDef = propertyDefinitions.find(pd => pd.uid === propertyUid)

            // Handle boolean values with semantic display
            if (propDef?.data_type === 'boolean' && typeof value === 'string') {
                const boolValue = value === 'true'
                const semantic = propDef.properties?.semantic as string
                return getBooleanDisplay(boolValue, semantic, t)
            }

            // Handle date values
            if (propDef?.data_type === 'date' && value && typeof value === 'string') {
                try {
                    const date = new Date(value)
                    if (!isNaN(date.getTime())) {
                        // If with_time is enabled, show full date and time, otherwise just date
                        if (propDef.properties?.with_time === true) {
                            return date.toLocaleString()
                        } else {
                            return date.toLocaleDateString()
                        }
                    }
                } catch {
                    // Fall back to string representation if parsing fails
                }
            }
        }

        return String(value)
    }


    const handleSaveField = async (field: 'start_date' | 'end_date' | 'name' | 'asset_type_uid' | 'external_code' | 'quantity', value: string | null) => {
        if (!assetData) return

        const setEditingField = field === 'name' ? setEditingNameField
            : field === 'external_code' ? setEditingExternalCodeField
                : field === 'quantity' ? setEditingQuantityField
                    : null

        // Clear any existing error for this field
        setFieldErrors(prev => ({ ...prev, [field]: '' }))

        try {
            let body: Record<string, unknown>
            if (field === 'asset_type_uid') {
                body = { asset_type_uid: value ?? '' }
            } else if (field === 'external_code') {
                body = { external_ids: { code: value ?? '' } }
            } else if (field === 'start_date' || field === 'end_date') {
                // Send explicit null when cleared; otherwise normalise to ISO date.
                const dateValue = value ? new Date(value).toISOString().split('T')[0] : null
                body = { [field]: dateValue }
            } else if (field === 'quantity') {
                const n = value ? parseInt(value, 10) : NaN
                if (!Number.isFinite(n) || n < 1) {
                    setFieldErrors(prev => ({ ...prev, quantity: t('detail.error_quantity_invalid', { defaultValue: 'Quantity must be a positive integer' }) }))
                    return
                }
                body = { quantity: n }
            } else {
                body = { [field]: value ?? '' }
            }

            await updateAssetMutation.mutateAsync(body)
            setEditingField?.(false)
        } catch (err: unknown) {
            // Handle error and display to user
            let errorMessage = t('detail.failed_update_field')
            const error = err as { response?: { status?: number; data?: { message?: string } } }

            if (error.response?.status === 409) {
                // Conflict error - likely duplicate external code
                if (field === 'external_code') {
                    errorMessage = t('detail.error_external_code_conflict')
                } else {
                    errorMessage = t('detail.error_conflict')
                }
            } else if (error.response?.data?.title) {
                errorMessage = error.response.data.title
            } else if (error && typeof error === 'object' && 'title' in error && typeof error.title === 'string') {
                errorMessage = error.title
            }

            setFieldErrors(prev => ({ ...prev, [field]: errorMessage }))
            // Keep editing state active on error so user can retry
        }
    }

    const handleCancelEdit = (field: 'name' | 'asset_type_uid' | 'external_code' | 'quantity') => {
        // Clear any error for this field when canceling
        setFieldErrors(prev => ({ ...prev, [field]: '' }))

        if (field === 'name') {
            setEditingName(assetData?.name || '')
            setEditingNameField(false)
        } else if (field === 'external_code') {
            setEditingExternalCode(assetData?.external_ids?.code || '')
            setEditingExternalCodeField(false)
        } else if (field === 'quantity') {
            setEditingQuantity(String(assetData?.quantity ?? 1))
            setEditingQuantityField(false)
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent, field: 'name' | 'asset_type_uid' | 'external_code' | 'quantity', value: string) => {
        if (e.key === 'Enter') {
            handleSaveField(field, value)
        } else if (e.key === 'Escape') {
            handleCancelEdit(field)
        }
    }

    const startEditing = (field: 'name' | 'asset_type_uid' | 'external_code' | 'quantity') => {
        // Clear any error for this field when starting to edit
        setFieldErrors(prev => ({ ...prev, [field]: '' }))

        if (field === 'name') {
            setEditingName(assetData?.name || '')
            setEditingNameField(true)
        } else if (field === 'external_code') {
            setEditingExternalCode(assetData?.external_ids?.code || '')
            setEditingExternalCodeField(true)
        } else if (field === 'quantity') {
            setEditingQuantity(String(assetData?.quantity ?? 1))
            setEditingQuantityField(true)
        }
    }




    const getAssetTypeFor = (uid: string | undefined) =>
        uid ? assetTypes.find(at => at.uid === uid) : undefined

    // Helper function to get asset type code for completion routing
    const getAssetTypeCode = (uid: string | undefined) => {
        if (!uid) return null
        const assetType = assetTypes.find(at => at.uid === uid)
        return assetType?.code || null
    }

    // Helper function to check if asset type should show pictures
    const shouldShowPicture = (uid: string | undefined) => {
        if (!uid) return false
        const assetType = assetTypes.find(at => at.uid === uid)
        return Boolean(assetType?.properties?.picture)
    }


    // Helper function to get address properties
    const getAddressIso20022Property = useCallback(() => {
        if (!properties || !propertyDefinitions) return null
        const prop = properties.find(p => {
            const pd = propertyDefinitions.find(d => d.uid === p.property_uid)
            return pd?.code === 'address_iso20022' && p.value
        })
        return prop?.value as Record<string, any> | null
    }, [properties, propertyDefinitions])

    const getAddressRawProperty = useCallback(() => {
        if (!properties || !propertyDefinitions) return null
        const prop = properties.find(p => {
            const pd = propertyDefinitions.find(d => d.uid === p.property_uid)
            return pd?.code === 'address_raw' && p.value
        })
        return typeof prop?.value === 'string' ? prop.value : null
    }, [properties, propertyDefinitions])

    // Geolocation property value — used to seed the address editor dialog
    // (the header no longer renders a map; that lives in the Site plan tab).
    const getGeolocationProperty = useCallback(() => {
        if (!properties || !propertyDefinitions) return null

        const geolocationProperty = properties.find(prop => {
            const propDef = propertyDefinitions.find(pd => pd.uid === prop.property_uid)
            return propDef?.code === 'geolocation' && prop.value
        })

        if (!geolocationProperty) return null

        return {
            value: geolocationProperty.value,
            propertyDefinition: propertyDefinitions.find(pd => pd.uid === geolocationProperty.property_uid)
        }
    }, [properties, propertyDefinitions])

    // Address editor dialog state
    const [pendingAddressData, setPendingAddressData] = useState<AddressData | null>(null)

    // Save handler for address editor opened from identity tab.
    const handleAddressSave = useCallback(async () => {
        if (!assetData?.uid || !pendingAddressData) return

        const setByCode = async (code: string, value: any) => {
            const { error } = await apiClient.PUT(
                '/api/v1/organizations/{orgCode}/assets/{assetUid}/properties/{propertyUid}',
                {
                    params: { path: { orgCode: org, assetUid: assetData.uid, propertyUid: code } },
                    body: { value },
                }
            )
            if (error) {
                throw new Error(`Failed to save property ${code}: ${JSON.stringify(error)}`)
            }
        }

        await setByCode('address_iso20022', pendingAddressData.iso20022)
        await setByCode('address_raw', pendingAddressData.raw)
        if (pendingAddressData.geolocation) {
            await setByCode('geolocation', pendingAddressData.geolocation)
        }
        if (pendingAddressData.placeId) {
            await setByCode('google_place_id', pendingAddressData.placeId)
        }

        refreshAssetProperties(queryClient, org, assetData.uid)
        setAddressEditorOpen(false)
    }, [assetData?.uid, org, queryClient, pendingAddressData])

    // Helper function to determine if asset is incomplete
    const isIncompleteAsset = () => {
        return assetData?.flags?.includes('incomplete') || false
    }

    // Helper function to get completion route based on asset type
    const getCompletionRoute = () => {
        const assetTypeCode = getAssetTypeCode(assetData?.asset_type_uid)
        switch (assetTypeCode) {
            case 'building_group':
                return '/$org/createBuildingGroup'
            case 'building':
                return '/$org/createBuilding'
            default:
                return null
        }
    }

    const _handleAddProperty = async () => {
        if (!newPropertyDefinitionUid || (Array.isArray(newPropertyValue) ? newPropertyValue.length === 0 : newPropertyValue === '')) {
            setAddPropertyError(t('detail.error_select_property_value'))
            return
        }

        setAddPropertyError('')

        try {
            const selectedPropDef = propertyDefinitions.find(pd => pd.uid === newPropertyDefinitionUid)
            let processedValue = newPropertyValue

            if (selectedPropDef) {
                switch (selectedPropDef.data_type) {
                    case 'number': {
                        if (Array.isArray(newPropertyValue)) {
                            setAddPropertyError(t('detail.error_multiple_not_supported_number'))
                            return
                        }
                        const numericValue = parseFloat(newPropertyValue)
                        if (isNaN(numericValue)) {
                            setAddPropertyError(t('detail.error_invalid_number'))
                            return
                        }

                        // Validate number constraints if defined
                        if (selectedPropDef.properties) {
                            if (selectedPropDef.properties.integer_only && !Number.isInteger(numericValue)) {
                                setAddPropertyError(t('detail.error_whole_number'))
                                return
                            }

                            const minValue = selectedPropDef.properties.min_value
                            if (typeof minValue === 'number' && numericValue < minValue) {
                                setAddPropertyError(t('detail.error_value_min', { min: minValue }))
                                return
                            }

                            const maxValue = selectedPropDef.properties.max_value
                            if (typeof maxValue === 'number' && numericValue > maxValue) {
                                setAddPropertyError(t('detail.error_value_max', { max: maxValue }))
                                return
                            }
                        }

                        // Store as string but validated as number
                        processedValue = newPropertyValue
                        break
                    }
                    case 'boolean':
                        if (Array.isArray(newPropertyValue)) {
                            setAddPropertyError(t('detail.error_multiple_not_supported_boolean'))
                            return
                        }
                        if (newPropertyValue === 'true' || newPropertyValue === 'false') {
                            processedValue = newPropertyValue
                        } else {
                            setAddPropertyError(t('detail.error_invalid_boolean'))
                            return
                        }
                        break
                    case 'date':
                        if (Array.isArray(newPropertyValue)) {
                            setAddPropertyError(t('detail.error_multiple_not_supported_date'))
                            return
                        }
                        if (newPropertyValue && !isNaN(Date.parse(newPropertyValue))) {
                            // Handle datetime-local input values which are in local timezone
                            if (selectedPropDef.properties?.with_time === true) {
                                // For datetime-local, the value is in format YYYY-MM-DDTHH:mm
                                // We need to interpret this as local time and convert to UTC
                                const dateValue = new Date(newPropertyValue)
                                processedValue = dateValue.toISOString()
                            } else {
                                // For date-only, just use the date part
                                const dateValue = new Date(newPropertyValue)
                                processedValue = dateValue.toISOString().split('T')[0]
                            }
                        } else {
                            setAddPropertyError(t('detail.error_invalid_date'))
                            return
                        }
                        break
                    case 'text': {
                        if (Array.isArray(newPropertyValue)) {
                            setAddPropertyError(t('detail.error_multiple_not_supported_text'))
                            return
                        }
                        processedValue = newPropertyValue
                        // Validate regex pattern if defined
                        const regexPattern = selectedPropDef.properties?.regex_pattern
                        if (typeof regexPattern === 'string' && regexPattern.trim()) {
                            try {
                                const regex = new RegExp(regexPattern)
                                if (!regex.test(processedValue)) {
                                    setAddPropertyError(t('detail.error_regex_pattern', { pattern: regexPattern }))
                                    return
                                }
                            } catch {
                                // If regex is invalid, just continue without validation
                                console.warn('Invalid regex pattern in property definition:', regexPattern)
                            }
                        }
                        break
                    }
                    case 'enum':
                        // For enum type, newPropertyValue can be string or string[] from EnumPropertyInput
                        if (selectedPropDef.properties?.multiple === true) {
                            // Multiple enum selection - keep as array or convert from string
                            if (Array.isArray(newPropertyValue)) {
                                processedValue = newPropertyValue
                            } else {
                                processedValue = newPropertyValue ? [newPropertyValue] : []
                            }
                        } else {
                            // Single enum value - convert array to string if needed
                            processedValue = Array.isArray(newPropertyValue) ? newPropertyValue[0] || '' : newPropertyValue
                        }
                        break
                    case 'json':
                    default:
                        processedValue = newPropertyValue
                        break
                }
            }

            const propertyData = {
                value: processedValue
            }

            await setPropertyValueMutation.mutateAsync({
                propertyUid: newPropertyDefinitionUid,
                propertyData
            })

            // Reset form
            setNewPropertyDefinitionUid('')
            setNewPropertyDefinitionName('')
            setNewPropertyValue('')
            setShowAddPropertyForm(false)
        } catch (err: unknown) {
            let errorMessage = t('detail.failed_add_property')
            const error = err as { response?: { data?: { message?: string } }; message?: string }

            if (error.response?.data?.title) {
                errorMessage = error.response.data.title
            } else if (error.title) {
                errorMessage = error.title
            }

            setAddPropertyError(errorMessage)
        }
    }

    const _handleCancelAddProperty = () => {
        setNewPropertyDefinitionUid('')
        setNewPropertyDefinitionName('')
        setNewPropertyValue('')
        setAddPropertyError('')
        setShowAddPropertyForm(false)
    }

    const getSelectedPropertyDefinition = () => {
        // First look in current asset's property definitions
        let propDef = propertyDefinitions.find(pd => pd.uid === newPropertyDefinitionUid)

        // If not found, look in recent property definitions (for adding new properties)
        if (!propDef) {
            propDef = recentPropertyDefinitions.find(pd => pd.uid === newPropertyDefinitionUid)
        }

        return propDef
    }


    const _startEditingProperty = (property: Record<string, unknown>) => {
        const propertyUid = (property.property_uid as string) || ''
        const rawValue = property.value
        let initialValue = ''

        // Format values for input fields based on type
        const propDef = propertyDefinitions.find(pd => pd.uid === propertyUid)

        // Handle enum arrays - convert to comma-separated string for editing
        if (propDef?.data_type === 'enum' && propDef.properties?.multiple === true && Array.isArray(rawValue)) {
            initialValue = rawValue.join(',')
        } else if (rawValue !== null && rawValue !== undefined) {
            initialValue = String(rawValue)
        }

        // Format date values for input fields
        if (propDef?.data_type === 'date' && initialValue) {
            try {
                const date = new Date(initialValue)
                if (!isNaN(date.getTime())) {
                    // If with_time is enabled, format as datetime-local, otherwise as date
                    if (propDef.properties?.with_time === true) {
                        // Format for datetime-local input in local timezone (YYYY-MM-DDTHH:mm)
                        const year = date.getFullYear()
                        const month = String(date.getMonth() + 1).padStart(2, '0')
                        const day = String(date.getDate()).padStart(2, '0')
                        const hours = String(date.getHours()).padStart(2, '0')
                        const minutes = String(date.getMinutes()).padStart(2, '0')
                        initialValue = `${year}-${month}-${day}T${hours}:${minutes}`
                    } else {
                        // Format for date input (YYYY-MM-DD) in local timezone
                        const year = date.getFullYear()
                        const month = String(date.getMonth() + 1).padStart(2, '0')
                        const day = String(date.getDate()).padStart(2, '0')
                        initialValue = `${year}-${month}-${day}`
                    }
                }
            } catch {
                // Keep original value if parsing fails
            }
        }

        setEditingPropertyUid(propertyUid)
        setEditingPropertyValue(initialValue)
        setPropertyEditErrors(prev => ({ ...prev, [propertyUid]: '' }))
    }

    const handleSavePropertyValue = async (propertyUid: string, value: string) => {
        setPropertyEditErrors(prev => ({ ...prev, [propertyUid]: '' }))

        try {
            // Find the property definition to check data type and process value accordingly
            const propDef = propertyDefinitions.find(pd => pd.uid === propertyUid)
            let processedValue: string | string[] = value

            if (propDef) {
                switch (propDef.data_type) {
                    case 'number': {
                        const numericValue = parseFloat(value)
                        if (isNaN(numericValue)) {
                            throw new Error(t('detail.error_invalid_number'))
                        }

                        // Validate number constraints if defined
                        if (propDef.properties) {
                            if (propDef.properties.integer_only && !Number.isInteger(numericValue)) {
                                throw new Error(t('detail.error_whole_number'))
                            }

                            const minValue = propDef.properties.min_value
                            if (typeof minValue === 'number' && numericValue < minValue) {
                                throw new Error(t('detail.error_value_min', { min: minValue }))
                            }

                            const maxValue = propDef.properties.max_value
                            if (typeof maxValue === 'number' && numericValue > maxValue) {
                                throw new Error(t('detail.error_value_max', { max: maxValue }))
                            }
                        }

                        // Store as string but validated as number
                        processedValue = value
                        break
                    }
                    case 'boolean':
                        if (value === 'true' || value === 'false') {
                            processedValue = value // Keep as string
                        } else {
                            throw new Error(t('detail.error_invalid_boolean'))
                        }
                        break
                    case 'date':
                        if (value && !isNaN(Date.parse(value))) {
                            // Handle datetime-local input values which are in local timezone
                            if (propDef.properties?.with_time === true) {
                                // For datetime-local, the value is in format YYYY-MM-DDTHH:mm
                                // We need to interpret this as local time and convert to UTC
                                const dateValue = new Date(value)
                                processedValue = dateValue.toISOString()
                            } else {
                                // For date-only, just use the date part
                                const dateValue = new Date(value)
                                processedValue = dateValue.toISOString().split('T')[0]
                            }
                        } else if (value) {
                            throw new Error(t('detail.error_invalid_date'))
                        }
                        break
                    case 'enum':
                        // For multiple enum values, convert comma-separated string back to array
                        if (propDef.properties?.multiple === true) {
                            processedValue = value ? value.split(',').map(v => v.trim()).filter(v => v) : []
                        } else {
                            processedValue = value
                        }
                        break
                    default:
                        // text, json - keep as string
                        processedValue = value

                        // Validate regex pattern for text type if defined
                        if (propDef.data_type === 'text') {
                            const regexPattern = propDef.properties?.regex_pattern
                            if (typeof regexPattern === 'string' && regexPattern.trim()) {
                                try {
                                    const regex = new RegExp(regexPattern)
                                    if (!regex.test(processedValue)) {
                                        throw new Error(t('detail.error_regex_pattern', { pattern: regexPattern }))
                                    }
                                } catch {
                                    // If regex is invalid, just continue without validation
                                    console.warn('Invalid regex pattern in property definition:', regexPattern)
                                }
                            }
                        }
                        break
                }
            }

            await updatePropertyValueMutation.mutateAsync({
                propertyUid,
                propertyData: { value: processedValue }
            })
            setEditingPropertyUid(null)
            setEditingPropertyValue('')
        } catch (err: unknown) {
            let errorMessage = t('detail.failed_update_property')
            const error = err as { response?: { data?: { message?: string } }; message?: string }

            if (error.response?.data?.title) {
                errorMessage = error.response.data.title
            } else if (error.title) {
                errorMessage = error.title
            }

            setPropertyEditErrors(prev => ({ ...prev, [propertyUid]: errorMessage }))
        }
    }

    const _handleDeletePropertyValue = async (propertyUid: string) => {
        if (!confirm('Are you sure you want to delete this property value?')) {
            return
        }

        try {
            await deletePropertyValueMutation.mutateAsync({ propertyUid })
        } catch (err: unknown) {
            let errorMessage = t('detail.failed_delete_property')
            const error = err as { response?: { data?: { message?: string } }; message?: string }
            if (error.response?.data?.title) {
                errorMessage = error.response.data.title
            } else if (error.title) {
                errorMessage = error.title
            }
            alert(errorMessage)
        }
    }

    const handleCancelPropertyEdit = () => {
        setEditingPropertyUid(null)
        setEditingPropertyValue('')
        setPropertyEditErrors({})
    }

    const _handlePropertyKeyDown = (e: React.KeyboardEvent, propertyUid: string, value: string) => {
        if (e.key === 'Enter') {
            handleSavePropertyValue(propertyUid, value)
        } else if (e.key === 'Escape') {
            handleCancelPropertyEdit()
        }
    }

    const _renderPropertyValueInput = () => {
        const selectedPropDef = getSelectedPropertyDefinition()

        if (!selectedPropDef) {
            return (
                <input
                    type="text"
                    value=""
                    onChange={() => { }} // No-op since it's disabled
                    className="w-full px-3 py-2 border border-border rounded shadow-sm text-sm bg-muted text-muted-foreground cursor-not-allowed"
                    placeholder={t('detail.select_property_first')}
                    disabled={true}
                />
            )
        }

        switch (selectedPropDef.data_type) {
            case 'number': {
                const step = selectedPropDef.properties?.integer_only ? '1' : 'any'
                const min = typeof selectedPropDef.properties?.min_value === 'number' ? selectedPropDef.properties.min_value : undefined
                const max = typeof selectedPropDef.properties?.max_value === 'number' ? selectedPropDef.properties.max_value : undefined

                return (
                    <input
                        type="number"
                        step={step}
                        min={min}
                        max={max}
                        value={newPropertyValue}
                        onChange={(e) => setNewPropertyValue(e.target.value)}
                        className="w-full px-3 py-2 border border-border rounded shadow-sm focus:outline-none focus:ring-ring focus:border-ring text-sm bg-background text-foreground"
                        placeholder={t('detail.enter_number_value')}
                    />
                )
            }
            case 'boolean': {
                const semantic = selectedPropDef.properties?.semantic as string
                const options = getBooleanOptions(semantic, t)
                return (
                    <select
                        value={newPropertyValue}
                        onChange={(e) => setNewPropertyValue(e.target.value)}
                        className="w-full px-3 py-2 border border-border rounded shadow-sm focus:outline-none focus:ring-ring focus:border-ring text-sm bg-background text-foreground"
                    >
                        <option value="">{t('detail.select_value')}</option>
                        {options.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                )
            }
            case 'date': {
                const withTime = selectedPropDef.properties?.with_time === true
                return (
                    <input
                        type={withTime ? "datetime-local" : "date"}
                        value={newPropertyValue}
                        onChange={(e) => setNewPropertyValue(e.target.value)}
                        onFocus={(e) => (e.currentTarget as any).showPicker?.()}
                        className="w-full px-3 py-2 border border-border rounded shadow-sm focus:outline-none focus:ring-ring focus:border-ring text-sm bg-background text-foreground"
                    />
                )
            }
            case 'enum':
                return <EnumPropertyInput propertyDefinitionUid={selectedPropDef.uid} value={newPropertyValue} onChange={setNewPropertyValue} org={org} />
            default: {
                // For text type, add live validation feedback
                if (selectedPropDef.data_type === 'text') {
                    const pattern = typeof selectedPropDef.properties?.regex_pattern === 'string' ? selectedPropDef.properties.regex_pattern : undefined
                    const textValue = Array.isArray(newPropertyValue) ? newPropertyValue.join(', ') : newPropertyValue
                    const isValid = validateTextPattern(textValue, pattern)
                    const hasPattern = !!pattern

                    return (
                        <div>
                            <input
                                type="text"
                                value={newPropertyValue}
                                onChange={(e) => setNewPropertyValue(e.target.value)}
                                className={`w-full px-3 py-2 border rounded shadow-sm focus:outline-none focus:ring-ring focus:border-ring text-sm bg-background text-foreground ${hasPattern ? (isValid ? 'border-green-500 focus:border-green-500' : 'border-orange-400 focus:border-orange-400') :
                                    'border-border'
                                    }`}
                                pattern={pattern}
                                title={pattern ? `Format: ${pattern}` : undefined}
                                placeholder={t('detail.enter_property_value')}
                            />
                            {hasPattern && (
                                <div className="flex items-center justify-between mt-1 text-xs">
                                    <span className="text-muted-foreground">
                                        Pattern: <code className="text-xs bg-muted px-1 py-0.5 rounded">{pattern}</code>
                                    </span>
                                    <span className={`flex items-center gap-1 ${isValid ? 'text-green-600' : 'text-orange-500'}`}>
                                        {isValid ? (
                                            <>
                                                <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
                                                {t('detail.valid')}
                                            </>
                                        ) : (
                                            <>
                                                <span className="inline-block w-2 h-2 bg-orange-400 rounded-full"></span>
                                                {t('detail.invalid_format')}
                                            </>
                                        )}
                                    </span>
                                </div>
                            )}
                        </div>
                    )
                }

                // Default case for other types
                const inputProps: Record<string, unknown> = {
                    type: "text",
                    value: newPropertyValue,
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setNewPropertyValue(e.target.value),
                    className: "w-full px-3 py-2 border border-border rounded shadow-sm focus:outline-none focus:ring-ring focus:border-ring text-sm bg-background text-foreground",
                    placeholder: t('detail.enter_property_value')
                }

                return <input {...inputProps} />
            }
        }
    }

    // Helper function to validate regex pattern in real-time
    const validateTextPattern = (value: string, pattern?: string): boolean => {
        if (!pattern || !pattern.trim()) return true
        try {
            const regex = new RegExp(pattern)
            return regex.test(value)
        } catch {
            return false
        }
    }


    const _renderEditPropertyInput = (propertyUid: string, value: string | string[], onChange: (value: string) => void, onKeyDown: (e: React.KeyboardEvent) => void) => {
        const propDef = propertyDefinitions.find(pd => pd.uid === propertyUid)

        if (!propDef) {
            return (
                <input
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={onKeyDown}
                    className={`flex-1 px-2 py-1 border ${propertyEditErrors[propertyUid] ? 'border-destructive' : 'border-border'} rounded text-sm bg-background text-foreground`}
                    disabled={updatePropertyValueMutation.isPending}
                    autoFocus
                />
            )
        }

        switch (propDef.data_type) {
            case 'number': {
                const step = propDef.properties?.integer_only ? '1' : 'any'
                const min = typeof propDef.properties?.min_value === 'number' ? propDef.properties.min_value : undefined
                const max = typeof propDef.properties?.max_value === 'number' ? propDef.properties.max_value : undefined

                return (
                    <input
                        type="number"
                        step={step}
                        min={min}
                        max={max}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        onKeyDown={onKeyDown}
                        className={`flex-1 px-2 py-1 border ${propertyEditErrors[propertyUid] ? 'border-destructive' : 'border-border'} rounded text-sm bg-background text-foreground`}
                        disabled={updatePropertyValueMutation.isPending}
                        autoFocus
                    />
                )
            }
            case 'boolean': {
                const semantic = propDef.properties?.semantic as string
                const options = getBooleanOptions(semantic, t)
                return (
                    <select
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        className={`flex-1 px-2 py-1 border ${propertyEditErrors[propertyUid] ? 'border-destructive' : 'border-border'} rounded text-sm bg-background text-foreground`}
                        disabled={updatePropertyValueMutation.isPending}
                        autoFocus
                    >
                        <option value="">{t('detail.select_value')}</option>
                        {options.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                )
            }
            case 'date': {
                const withTime = propDef.properties?.with_time === true
                return (
                    <input
                        type={withTime ? "datetime-local" : "date"}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        onKeyDown={onKeyDown}
                        onFocus={(e) => (e.currentTarget as any).showPicker?.()}
                        className={`flex-1 px-2 py-1 border ${propertyEditErrors[propertyUid] ? 'border-destructive' : 'border-border'} rounded text-sm bg-background text-foreground`}
                        disabled={updatePropertyValueMutation.isPending}
                        autoFocus
                    />
                )
            }
            case 'enum':
                return <EnumPropertyEditInput
                    propertyDefinitionUid={propertyUid}
                    value={value}
                    onChange={(newValue) => onChange(Array.isArray(newValue) ? newValue.join(',') : newValue)}
                    disabled={updatePropertyValueMutation.isPending}
                    hasError={!!propertyEditErrors[propertyUid]}
                    org={org}
                />
            default: {
                // For text type, add live validation feedback
                if (propDef.data_type === 'text') {
                    const pattern = typeof propDef.properties?.regex_pattern === 'string' ? propDef.properties.regex_pattern : undefined
                    const textValue = Array.isArray(value) ? value.join(', ') : value
                    const isValid = validateTextPattern(textValue, pattern)
                    const hasPattern = !!pattern

                    return (
                        <div className="flex-1">
                            <input
                                type="text"
                                value={value}
                                onChange={(e) => onChange(e.target.value)}
                                onKeyDown={onKeyDown}
                                className={`w-full px-2 py-1 border rounded text-sm bg-background text-foreground ${propertyEditErrors[propertyUid] ? 'border-destructive' :
                                    hasPattern ? (isValid ? 'border-green-500' : 'border-orange-400') :
                                        'border-border'
                                    }`}
                                pattern={pattern}
                                title={pattern ? `Format: ${pattern}` : undefined}
                                disabled={updatePropertyValueMutation.isPending}
                                autoFocus
                            />
                            {hasPattern && (
                                <div className="flex items-center justify-between mt-1 text-xs">
                                    <span className="text-muted-foreground">
                                        Pattern: <code className="text-xs bg-muted px-1 py-0.5 rounded">{pattern}</code>
                                    </span>
                                    <span className={`flex items-center gap-1 ${isValid ? 'text-green-600' : 'text-orange-500'}`}>
                                        {isValid ? (
                                            <>
                                                <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
                                                {t('detail.valid')}
                                            </>
                                        ) : (
                                            <>
                                                <span className="inline-block w-2 h-2 bg-orange-400 rounded-full"></span>
                                                {t('detail.invalid_format')}
                                            </>
                                        )}
                                    </span>
                                </div>
                            )}
                        </div>
                    )
                }

                // Default case for other types
                const inputProps: Record<string, unknown> = {
                    type: "text",
                    value: value,
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value),
                    onKeyDown: onKeyDown,
                    className: `flex-1 px-2 py-1 border ${propertyEditErrors[propertyUid] ? 'border-destructive' : 'border-border'} rounded text-sm bg-background text-foreground`,
                    disabled: updatePropertyValueMutation.isPending,
                    autoFocus: true
                }

                return <input {...inputProps} />
            }
        }
    }


    if (loading) {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-center py-12">
                    <div className="text-lg text-muted-foreground">{t('detail.loading_asset')}</div>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="space-y-6">
                <div className="flex justify-between items-center">
                    <h1 className="text-2xl font-bold text-foreground">{t('detail.asset_details')}</h1>
                </div>

                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-destructive/60" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-destructive">{t('detail.error_loading')}</h3>
                            <p className="text-sm text-destructive mt-1">{error}</p>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if (!assetData) {
        return (
            <div className="space-y-6">
                <div className="flex justify-between items-center">
                    <h1 className="text-2xl font-bold text-foreground">{t('detail.asset_details')}</h1>
                </div>

                <div className="flex flex-col items-center gap-4 py-12 text-center">
                    <AppIcon path={SECTION_ICONS.assets} size="2xl" tone="muted" className="opacity-40" />
                    <p className="text-muted-foreground">{t('detail.asset_not_found')}</p>
                    <Link to="/$org/assets" params={{ org }}>
                        <Button variant="outline" size="sm" data-testid="asset-not-found-back-btn">
                            {t('detail.back_to_assets')}
                        </Button>
                    </Link>
                </div>
            </div>
        )
    }

    return (
        <div className="flex h-full min-h-[calc(100vh-120px)]">
            {/* The asset-hierarchy tree lives only in the "Actifs" list route,
                not on the asset detail view. */}
            <div className="flex-1 space-y-6 px-4 overflow-x-hidden">

            {/* Header */}
            <div>
                {/* Top row with title and buttons. flex-wrap + ml-auto on the
                    action group: when both side panels are open the center
                    column can drop under ~540px, and a non-wrapping row makes
                    the action buttons overflow under the right panel. */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-4">
                    {/* The identity group is a named hook so a test can reach
                        THIS tile: `asset-type-tile` is emitted by every tile on
                        the page (the relation rows draw them too), so a bare
                        getByTestId would be strict-mode ambiguous as soon as the
                        asset has one relation.

                        The `order-*` / `basis-full` pair is what makes the row
                        wrap into THREE lines on a phone rather than four
                        (spec 2026-09-17-06): identity, then the tags, then the
                        actions — each taking the whole row below `md` and
                        falling back onto one line above it. */}
                    <div
                        className="order-1 flex basis-full items-center gap-3 min-w-0 md:basis-auto"
                        data-testid="asset-header-identity"
                    >
                        {/* The tile is decoration, not a control: changing the
                            type happens in the Data / Properties tab, next to the
                            rest of the asset's identity. The type chip on the
                            right of the title row — not this — names the type. */}
                        <AssetTypeTile
                            type={getAssetTypeFor(assetData.asset_type_uid)}
                            asset={assetData}
                            size="lg"
                        />
                        {editingNameField ? (
                            <div className="flex items-center gap-1 flex-1 min-w-0">
                                <input
                                    type="text"
                                    value={editingName}
                                    onChange={(e) => setEditingName(e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(e, 'name', editingName)}
                                    className={`text-xl sm:text-2xl font-bold text-foreground bg-background border rounded px-2 py-0.5 flex-1 min-w-0 focus:outline-none focus:ring-2 focus:ring-ring ${fieldErrors['name'] ? 'border-destructive' : 'border-input'}`}
                                    autoFocus
                                    disabled={updateAssetMutation.isPending}
                                    aria-label={t('detail.asset_name')}
                                    data-testid="asset-name-input"
                                />
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleSaveField('name', editingName)}
                                    disabled={updateAssetMutation.isPending}
                                    aria-label={t('detail.save', { defaultValue: 'Save' })}
                                    title={t('detail.save', { defaultValue: 'Save' })}
                                    data-testid="asset-name-save-btn"
                                >
                                    <AppIcon path={mdiCheck} size="md" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleCancelEdit('name')}
                                    disabled={updateAssetMutation.isPending}
                                    aria-label={t('detail.cancel', { defaultValue: 'Cancel' })}
                                    title={t('detail.cancel', { defaultValue: 'Cancel' })}
                                    data-testid="asset-name-cancel-btn"
                                >
                                    <AppIcon path={mdiClose} size="md" />
                                </Button>
                            </div>
                        ) : (
                            <h1
                                className="min-w-0 truncate text-xl sm:text-2xl font-bold text-foreground cursor-pointer hover:underline"
                                onClick={() => startEditing('name')}
                                title={t('detail.click_to_rename')}
                                data-testid="asset-name-display"
                            >
                                <AssetName name={getAssetTitle(assetData)} archivedAt={assetData.archived_at} />
                            </h1>
                        )}
                        {fieldErrors['name'] && !editingNameField && (
                            <span className="text-xs text-destructive ml-2">{fieldErrors['name']}</span>
                        )}
                        {typeof assetData.quantity === 'number' && assetData.quantity > 1 && (
                            <span
                                className="text-sm font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded whitespace-nowrap"
                                data-testid="asset-quantity-badge"
                                title={t('detail.quantity')}
                            >
                                {t('detail.quantity_display', { count: assetData.quantity })}
                            </span>
                        )}
                        {kindShell.assetManagerChrome && (
                        <button
                            onClick={(e) => { e.stopPropagation(); toggleFavorite(assetData.uid) }}
                            className={`transition-colors ${isFavorite(assetData.uid) ? 'text-yellow-500 hover:text-yellow-400 dark:text-foreground dark:hover:text-muted-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                            data-testid="favorite-toggle"
                            title={isFavorite(assetData.uid) ? t('detail.remove_favorite') : t('detail.add_favorite')}
                        >
                            <AppIcon path={isFavorite(assetData.uid) ? mdiStar : mdiStarOutline} size="lg" />
                        </button>
                        )}

                        {/* font-mono belongs on the id *values* only. */}
                        <div className="hidden lg:flex flex-wrap items-center gap-2 text-sm text-foreground">
                            {/* External-id chips: the `code` chip is the canonical "external
                                code" surface; other ids (RNB link, secondary codes) keep their
                                existing semantics. */}
                            {/* Read-only external-id chips. Editing the code and the lifecycle
                                dates moved to the Data tab's Identité section (CoreRow), so the
                                header only *shows* identifiers — it no longer edits them. */}
                            {assetData.external_ids && Object.entries(assetData.external_ids).map(([key, value]) => {
                                if (!value) return null
                                if (key.toUpperCase() === 'RNB') {
                                    return (
                                        <a
                                            key={key}
                                            href={`https://rnb.beta.gouv.fr/carte?q=${encodeURIComponent(value)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center h-8 bg-primary/10 hover:bg-primary/20 px-3 rounded-md text-xs font-mono text-primary underline transition-colors gap-1.5 whitespace-nowrap"
                                        >
                                            <AppIcon path={mdiIdentifier} size="xs" />
                                            <span>{key}:{value}</span>
                                        </a>
                                    )
                                }
                                return (
                                    <button
                                        key={key}
                                        onClick={() => navigate({ to: '/$org/assets/$assetUid', params: { org, assetUid: `${key}:${value}` } })}
                                        className="inline-flex items-center h-8 bg-primary/10 hover:bg-primary/20 px-3 rounded-md text-xs font-mono text-primary cursor-pointer transition-colors gap-1.5 whitespace-nowrap"
                                        data-testid={key === 'code' ? 'external-code-chip' : undefined}
                                    >
                                        <AppIcon path={mdiIdentifier} size="xs" />
                                        <span>{key}:{value}</span>
                                    </button>
                                )
                            })}
                        </div>
                    </div>

                    {/* Its own line below `md`: three tags used to push the
                        action buttons a full line down (the AFUL page), so
                        below `md` only two render and a `+N` chip opens the
                        rest in place. */}
                    <EntityTagsSection
                        org={org}
                        entityType="asset"
                        entityUid={assetData.uid!}
                        className="order-2 flex basis-full flex-wrap items-center gap-1 md:basis-auto"
                        collapse={{ below: 'md', max: 2 }}
                        editable={canWriteAssets}
                    />

                    <div className={`order-3 flex basis-full flex-wrap items-center gap-2 md:ml-auto md:basis-auto ${kindShell.assetManagerChrome ? 'min-h-10' : ''}`}>
                        {/* View this asset as the focal node of the graph explorer. */}
                        {assetData?.uid && kindShell.assetManagerChrome && (
                            <Link
                                to="/$org/graph"
                                params={{ org }}
                                search={{ focus: assetData.uid, expanded: undefined }}
                            >
                                <Button
                                    size="icon"
                                    variant="outline"
                                    data-testid="view-in-graph-btn"
                                    aria-label={t('graph:action.view_in_graph')}
                                    title={t('graph:action.view_in_graph')}
                                >
                                    <AppIcon path={mdiGraphOutline} size="sm" />
                                </Button>
                            </Link>
                        )}

                        {/* The QR sticker. A header action rather than a tab:
                            there is one code and two things to do with it, which
                            is not something to browse. */}
                        {assetData?.uid && kindShell.assetManagerChrome && <AssetLabelsButton org={org} assetUid={assetData.uid} />}

                        {/* Who has access, and from where (spec 2026-09-04-03):
                            a header button since spec 2026-10-25-20, it was a
                            tab. Only for the people who manage access. */}
                        {assetData?.uid && canReadMembers && (
                            <AssetAccessButton
                                org={org}
                                assetUid={assetData.uid}
                                assetName={getAssetTitle(assetData)}
                                initialOpen={new URLSearchParams(window.location.search).get('tab') === 'access'}
                            />
                        )}

                        {/* Completion Button for Incomplete Assets */}
                        {isIncompleteAsset() && getCompletionRoute() && (
                            <Link
                                to={getCompletionRoute()!}
                                params={{ org }}
                                search={{
                                    asset_uid: assetData?.uid,
                                    step: ((assetData?.frontend_data as any)?.last_wizard_step ?? 0) + 1,
                                }}
                            >
                                <Button
                                    size="sm"
                                    variant="default"
                                    aria-label={t('detail.complete')}
                                    title={t('detail.complete')}
                                >
                                    <AppIcon path={mdiWrench} size="sm" className="md:mr-1" />
                                    <span className="hidden md:inline">{t('detail.complete')}</span>
                                </Button>
                            </Link>
                        )}

                        {/* Moving and archiving are rare, and archiving is
                            destructive: behind the « … » menu (patterns §33)
                            rather than a red button that outweighs everything
                            the page is for (spec 2026-10-25-20). Moving shares
                            its save path with the identity row of the registry. */}
                        {assetData?.uid && !assetData.archived_at && canWriteAssets && (
                            <>
                                <OverflowMenu testId="asset-more-actions" label={t('detail.more_actions')}>
                                    <OverflowMenuItem
                                        icon={mdiFileTreeOutline}
                                        label={t('move.button')}
                                        onSelect={() => setHeaderDialog('move')}
                                        testId="move-asset-btn"
                                    />
                                    <OverflowMenuSeparator />
                                    <OverflowMenuItem
                                        icon={mdiArchiveArrowDownOutline}
                                        label={t('archive.button')}
                                        destructive
                                        onSelect={() => setHeaderDialog('archive')}
                                        testId="archive-asset-btn"
                                    />
                                </OverflowMenu>
                                <AssetMoveDialog
                                    org={org}
                                    assetUid={assetData.uid as string}
                                    parentAssetUid={(assetData.parent_asset_uid as string) || null}
                                    parentAssetName={(parentAsset?.name as string) || (parentAsset?.code as string) || null}
                                    open={headerDialog === 'move'}
                                    onOpenChange={(o) => setHeaderDialog(o ? 'move' : null)}
                                />
                                <AssetArchiveDialog
                                    asset={assetData}
                                    org={org}
                                    open={headerDialog === 'archive'}
                                    onOpenChange={(o) => setHeaderDialog(o ? 'archive' : null)}
                                    onArchived={() => {
                                        if (assetData.parent_asset_uid) {
                                            navigate({ to: '/$org/assets/$assetUid', params: { org, assetUid: assetData.parent_asset_uid as string } })
                                        } else {
                                            navigate({ to: '/$org/assets', params: { org } })
                                        }
                                    }}
                                />
                            </>
                        )}

                        {assetData && assetData.archived_at && canWriteAssets && (
                            <Button
                                variant="outline"
                                size="sm"
                                data-testid="restore-asset-btn"
                                aria-label={t('archive.restore_button')}
                                title={t('archive.restore_button')}
                                onClick={async () => {
                                    await restoreAssetMutation.mutateAsync({ assetUid: assetData.uid })
                                }}
                            >
                                <AppIcon path={mdiArchiveArrowUpOutline} size="sm" className="md:mr-1" />
                                <span className="hidden md:inline">{t('archive.restore_button')}</span>
                            </Button>
                        )}

                        {assetData && assetData.archived_at && canWriteAssets && (
                            <AssetDeleteDialog
                                asset={assetData}
                                org={org}
                                onDeleted={() => {
                                    if (assetData.parent_asset_uid) {
                                        navigate({ to: '/$org/assets/$assetUid', params: { org, assetUid: assetData.parent_asset_uid as string } })
                                    } else {
                                        navigate({ to: '/$org/assets', params: { org } })
                                    }
                                }}
                            />
                        )}

                    </div>
                </div>

                {/* Spec 01 §1 — Identity / Location / Volumetry sub-row.
                    Omitted for buildings: the location slot is the address, which
                    already appears in the address box below, and the surface lives
                    in the Data tab — so this line would be pure duplication. */}
                {assetTypeCode !== 'building' && (() => {
                    const propsByCode: Record<string, any> = {}
                    for (const p of properties) {
                        const pd = propertyDefinitions.find((d) => d.uid === p.property_uid)
                            || recentPropertyDefinitions.find((d) => d.uid === p.property_uid)
                        const code = pd?.code
                        if (code && p.value != null) propsByCode[code] = p.value
                    }
                    const addressShort = (() => {
                        const v = propsByCode.address_raw
                        if (typeof v === 'string' && v) return v
                        return null
                    })()
                    const slots = getAssetHeaderSlots({
                        asset: assetData,
                        assetTypeCode,
                        parentAsset,
                        parentChain: assetChainData,
                        propsByCode,
                        addressShort,
                    })
                    // The external-id is already rendered as a chip on the title row,
                    // so we deliberately omit slots.identity?.external here to avoid
                    // duplication.
                    // The location slot is the parent chain, which the top-bar
                    // breadcrumb already spells out — a second breadcrumb under the
                    // title, editable or not. Parent editing moved to the identity
                    // rows of the property registry (useAssetCoreRows), where the
                    // asset type, code and lifecycle dates already live.
                    if (!slots.volumetry) return null
                    return (
                        <div className="text-sm text-muted-foreground mb-3 flex flex-wrap items-center gap-x-3 gap-y-1">
                            {slots.volumetry && (
                                <span data-testid="asset-header-volumetry">
                                    {slots.volumetry.value}
                                    {slots.volumetry.label ? ` · ${slots.volumetry.label}` : ''}
                                </span>
                            )}
                        </div>
                    )
                })()}

                {/* Archived banner */}
                {assetData?.archived_at && (
                    <div data-testid="asset-archived-banner" className="bg-muted border border-border rounded-md p-3 flex items-center gap-2 text-muted-foreground">
                        <AppIcon path={mdiArchiveArrowUpOutline} size="md" />
                        <span>
                            {t('archive.banner', {
                                date: new Date(assetData.archived_at).toLocaleDateString(),
                            })}
                        </span>
                    </div>
                )}

                {/* Approval banner — an asset carrying `to_approve` is a
                    proposal, not a confirmed record, and used to render exactly
                    like a confirmed one (spec 2026-07-30-02). Same slot and
                    shape as the archived banner above; renders nothing at all
                    for a confirmed asset. */}
                {assetData && (
                    <AssetApprovalBanner
                        org={org}
                        assetUid={assetData.uid}
                        flags={assetData.flags}
                        sourceCode={assetData.source_code}
                        sourceDetails={assetData.source_details}
                        sourceJobUid={assetData.source_job_uid}
                        approvedAt={assetData.approved_at}
                        approvedByName={assetData.approved_by_name}
                        approvalReason={assetData.approval_reason}
                        entityExtractionPendingDocumentCount={assetData.entity_extraction_pending_document_count}
                    />
                )}

                {/* Below-title context block. Per spec 2026-05-08-asset-header-
                    slim-down (with subsequent live iteration), this block hosts the
                    asset's spatial context — picture (rare; only for asset types
                    whose ontology marks `properties.picture`), the compact address
                    bar, the map (only when a geolocation property is set), and the
                    quantity row (only when the type allows multiple). Name /
                    external code / asset type live on the title row; parent lives
                    on the muted sub-row above; effective + expiration live as a
                    lifecycle chip on the title row. The block sits above the tab
                    strip so it shows once at the top of the page, not per tab. */}
                {(() => {
                    const hasSummaryContent =
                        shouldShowPicture(assetData?.asset_type_uid) ||
                        assetTypeCode === 'building_group' ||
                        hasAddressFlag
                    return assetData && hasSummaryContent
                })() && (
                    <div
                        className="bg-card rounded-lg border border-border p-3 mb-3"
                        data-testid="asset-summary-header"
                    >
                        {(() => {
                            // The location map lives in the Site plan tab, and the
                            // quantity moved into the property registry's identity
                            // rows — so nothing folds any more and the block is a
                            // single row: picture (rare) plus the compact address.
                            return (
                                <div className="space-y-2">
                                    <div className="flex items-center gap-3">
                                        {shouldShowPicture(assetData.asset_type_uid) && (
                                            <div className="flex-shrink-0">
                                                <EntityPicture
                                                    entityType="asset"
                                                    entityId={assetData.uid}
                                                    orgCode={org}
                                                    size="100x100"
                                                    documentUid={assetData.picture?.document_uid}
                                                    editable={true}
                                                    className="w-16 h-16 rounded"
                                                    alt={`${getAssetTitle(assetData)} picture`}
                                                    onUpload={(success, error) => {
                                                        if (success) {
                                                            queryClient.invalidateQueries({
                                                                queryKey: queryKeys.asset(org, assetData?.uid),
                                                            });
                                                        } else {
                                                            console.error('Failed to update asset picture:', error);
                                                        }
                                                    }}
                                                />
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            {assetTypeCode === 'building_group' ? (
                                                // A site shows its first building's address
                                                // (read-only); only its own fallback address
                                                // is editable, through the shared dialog.
                                                <SiteAddressDisplay
                                                    org={org}
                                                    siteUid={assetData.uid}
                                                    onEdit={() => setAddressEditorOpen(true)}
                                                />
                                            ) : hasAddressFlag ? (
                                                <AddressCompactDisplay
                                                    addressIso20022={getAddressIso20022Property()}
                                                    addressRaw={getAddressRawProperty()}
                                                    onEdit={() => setAddressEditorOpen(true)}
                                                />
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            )
                        })()}
                    </div>
                )}

                {/* Address editor dialog — mounted at the page level so the
                    pinned summary header (and any other tab content) can open
                    it without remounting per tab. */}
                <Dialog open={addressEditorOpen} onOpenChange={setAddressEditorOpen}>
                    <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="address-editor-dialog">
                        <DialogHeader>
                            <DialogTitle>{t('detail.edit_address')}</DialogTitle>
                        </DialogHeader>
                        <AddressSearch
                            initialAddress={getAddressIso20022Property()}
                            initialRaw={getAddressRawProperty() || undefined}
                            initialGeolocation={(() => {
                                const geo = getGeolocationProperty()
                                if (!geo?.value || typeof geo.value !== 'object') return undefined
                                const v = geo.value as Record<string, any>
                                return 'lat' in v && 'lng' in v ? { lat: Number(v.lat), lng: Number(v.lng) } : undefined
                            })()}
                            autoSearch={!(() => {
                                const geo = getGeolocationProperty()
                                return geo?.value && typeof geo.value === 'object'
                            })()}
                            onChange={setPendingAddressData}
                        />
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setAddressEditorOpen(false)} data-testid="address-cancel-btn">
                                {t('common:actions.cancel', 'Cancel')}
                            </Button>
                            <Button onClick={handleAddressSave} data-testid="address-save-btn">
                                {t('common:actions.save', 'Save')}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {canUseAgentRuns && assetTypeCode === 'building_group' && assetData?.uid && (
                    <div className="mb-4">
                        <AgentRunsPanel org={org} targetEntityType="asset" targetUid={assetData.uid} missionTarget="site" compact />
                    </div>
                )}

                {/* Tabs - Responsive (text-only labels per design system) */}
                <div className="border-b border-border">
                    {/* Desktop: Horizontal tabs grouped into intention clusters.
                        The group label carries the meaning, the gap carries the
                        grouping — a label + its cluster scroll together as one
                        unit. Grouping is visual only: a single role="tablist" with
                        one active tab and arrow-key navigation across all tabs. */}
                    <div className="hidden sm:block">
                        <nav
                            role="tablist"
                            aria-label={t('detail.tabs_aria_label')}
                            className="-mb-px flex w-full overflow-x-auto [scrollbar-width:thin]"
                        >
                            {tabGroups.map((group, groupIndex) => (
                                <div
                                    key={group.id}
                                    role="presentation"
                                    className={`flex flex-1 flex-col px-4 ${groupIndex > 0 ? 'border-l border-border' : ''}`}
                                >
                                    <span
                                        aria-hidden="true"
                                        className="mb-1 text-center text-[10px] font-medium uppercase leading-4 tracking-[0.14em] text-muted-foreground/80"
                                    >
                                        {t(group.labelKey)}
                                    </span>
                                    <div role="group" aria-label={t(group.labelKey)} className="flex justify-center gap-4">
                                        {group.items.map((tab) => renderTabButton(tab))}
                                    </div>
                                </div>
                            ))}
                            {ungroupedTabs.length > 0 && (
                                <div role="presentation" className="flex flex-1 flex-col border-l border-border px-4">
                                    <span aria-hidden="true" className="mb-1 text-center text-[10px] leading-4">&nbsp;</span>
                                    <div className="flex justify-center gap-4">
                                        {ungroupedTabs.map((tab) => renderTabButton(tab))}
                                    </div>
                                </div>
                            )}
                        </nav>
                    </div>

                    {/* Mobile: Select dropdown — same grouped order, group name
                        shown as a non-selectable section label. */}
                    <div className="sm:hidden">
                        <Select
                            value={effectiveTab}
                            onValueChange={handleTabChange}
                        >
                            <SelectTrigger className="w-full h-12 bg-background border-0 border-b-2 border-primary rounded-none focus:ring-0 focus:border-primary">
                                <SelectValue placeholder={t('detail.select_tab')} />
                            </SelectTrigger>
                            <SelectContent>
                                {tabGroups.map((group) => (
                                    <SelectGroup key={group.id}>
                                        <SelectLabel className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                                            {t(group.labelKey)}
                                        </SelectLabel>
                                        {group.items.map((tab) => (
                                            <SelectItem key={tab.id} value={tab.id} data-testid={`tab-select-${tab.id}`}>
                                                <span className="inline-flex items-center gap-1.5">
                                                    {tab.label}
                                                    {tab.badge != null && (
                                                        <span className="inline-flex min-w-4 items-center justify-center rounded-full bg-warning/15 px-1.5 text-[11px] font-bold text-warning" data-testid={`tab-select-badge-${tab.id}`}>
                                                            {tab.badge}
                                                        </span>
                                                    )}
                                                </span>
                                            </SelectItem>
                                        ))}
                                    </SelectGroup>
                                ))}
                                {(ungroupedTabs.length > 0 || tabs.some((tab) => tab.mobileOnly)) && (
                                    <SelectGroup>
                                        {[...ungroupedTabs, ...tabs.filter((tab) => tab.mobileOnly)].map((tab) => (
                                            <SelectItem key={tab.id} value={tab.id} data-testid={`tab-select-${tab.id}`}>
                                                <span className="inline-flex items-center gap-1.5">
                                                    {tab.label}
                                                    {tab.badge != null && (
                                                        <span className="inline-flex min-w-4 items-center justify-center rounded-full bg-warning/15 px-1.5 text-[11px] font-bold text-warning" data-testid={`tab-select-badge-${tab.id}`}>
                                                            {tab.badge}
                                                        </span>
                                                    )}
                                                </span>
                                            </SelectItem>
                                        ))}
                                    </SelectGroup>
                                )}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </div>

            {/* Resume-onboarding banner — shown on a building-group page while
                its onboarding wizard is unfinished (frontend_data.incomplete).
                Fiabilisation of extracted data lives strictly in the wizard
                (spec 2026-07-09-fiabilisation-onboarding-only), so this banner
                is the recall path back into it. */}
            {getAssetTypeCode(assetData?.asset_type_uid) === 'building_group'
                && (assetData?.frontend_data as { incomplete?: boolean } | undefined)?.incomplete === true
                && assetData?.uid && (
                <div
                    className="mt-4 flex flex-col gap-3 rounded-lg border border-warning/25 bg-warning/10 p-4 sm:flex-row sm:items-center sm:justify-between"
                    data-testid="resume-onboarding-banner"
                >
                    <div className="flex items-start gap-3">
                        <AppIcon path={mdiPlayCircleOutline} size="lg" className="mt-0.5 flex-shrink-0 text-warning" />
                        <div className="min-w-0">
                            <div className="text-sm font-semibold text-foreground">{t('detail.resume_onboarding_title')}</div>
                            <div className="text-sm text-muted-foreground">{t('detail.resume_onboarding_desc')}</div>
                        </div>
                    </div>
                    <Link
                        to="/$org/createBuilding"
                        params={{ org }}
                        search={{
                            asset_uid: assetData.uid,
                            step: (assetData.frontend_data as { last_wizard_step?: number } | undefined)?.last_wizard_step ?? 3,
                        }}
                        className="flex-shrink-0"
                        data-testid="resume-onboarding-btn"
                    >
                        <Button size="sm" variant="default">
                            <AppIcon path={mdiPlayCircleOutline} size="sm" className="mr-1" />
                            {t('detail.resume_onboarding_cta')}
                        </Button>
                    </Link>
                </div>
            )}

            {/* Tab Content */}
            {effectiveTab === 'properties' && (
                <div className="space-y-6">
                    {/* Cost of ownership (spec 2026-08-24-01, lot 2). Equipment
                        only: a lot has no replacement cost to measure against,
                        and a ratio with no denominator is not a number. */}
                    {isMeterAsset && assetData?.uid && (
                        <MeterSupplyCard org={org} assetUid={assetData.uid} />
                    )}
                    {/* Dated readings and monthly consumption (spec 2026-10-27-18). */}
                    {isMeterAsset && assetData?.uid && (
                        <MeterReadingsCard org={org} assetUid={assetData.uid} />
                    )}
                    {isEquipmentAsset && assetData?.uid && (
                        <CostOfOwnershipCard org={org} assetUid={assetData.uid} />
                    )}
                    {/* Its components, or the equipment it is part of (spec 2026-10-27-17). */}
                    {isEquipmentAsset && assetData?.uid && !assetTypeCode?.endsWith('_network') && (
                        <ComponentsCard org={org} assetUid={assetData.uid} assetName={assetData.name ?? undefined} />
                    )}
                    {/* The floors whose plans draw this network (spec 2026-10-20-33). */}
                    {assetTypeCode?.endsWith('_network') && assetData?.uid && (
                        <NetworkPlansCard org={org} assetUid={assetData.uid} />
                    )}
                    {/* Its floor segments, or the building network it is part of (spec 2026-10-20-35). */}
                    {assetTypeCode?.endsWith('_network') && assetData?.uid && (
                        <NetworkSegmentsCard org={org} assetUid={assetData.uid} />
                    )}
                    {/* Properties (Contacts/Children/Notes are in the right panel; left tree handles cross-portfolio nav) */}
                    <AssetProperties
                        org={org}
                        assetUid={assetData.uid}
                        categoryCodes={propertiesTabCategories}
                        baseCodes={propertiesTabBaseCodes}
                        groupByCategory
                        hideCategoryHeaders
                        categoryOrder={[...DATA_CATEGORY_ORDER]}
                        // Equipment and components have no Data tab, so this is
                        // where their core columns (type, external code, dates)
                        // are edited — the header only shows them.
                        coreRows={coreRows}
                        onSaveCoreRow={onSaveCoreRow}
                        onPropertyUpdated={() => {
                            queryClient.invalidateQueries({ queryKey: queryKeys.asset(org, assetData.uid) })
                        }}
                    />
                    {/* Typed cross-asset links (chimney → boiler, riser
                        serves lot, radiator part of heating network, …).
                        Rendered here so links are managed from the same panel
                        where the rest of the asset's properties are edited.
                        See spec 2026-08-16-03-asset-relations-management-ui. */}
                    <RelationsPanel
                        org={org}
                        assetUid={assetData.uid}
                        assetTypeUid={currentAssetType?.uid}
                        relations={(assetData as any)?.relations}
                    />
                    {!isEquipmentAsset && <AssetChildrenSection org={org} assetUid={assetData.uid} />}
                </div>
            )}

            {effectiveTab === 'photo' && assetData?.uid && (
                <AssetPhotoTab
                    org={org}
                    assetUid={assetData.uid}
                    assetName={getAssetTitle(assetData)}
                    pictureDocumentUid={assetData.picture?.document_uid}
                />
            )}

            {effectiveTab === 'plan' && canUseAgentRuns && assetData?.uid && (assetTypeCode === 'building' || assetTypeCode === 'floor') && (
                <div className="mb-4">
                    <AgentRunsPanel org={org} targetEntityType="asset" targetUid={assetData.uid} missionTarget={assetTypeCode} />
                </div>
            )}
            {effectiveTab === 'plan' && assetData?.uid && (
                assetTypeCode === 'building'
                    ? <BuildingPlanTab org={org} buildingUid={assetData.uid} />
                    : <AssetPlanViewer org={org} assetUid={assetData.uid} assetTypeCode={assetTypeCode} />
            )}

            {effectiveTab === 'composite-plan' && assetData?.uid && (
                <Suspense fallback={<div className="flex h-[560px] items-center justify-center rounded-lg border border-border bg-card text-muted-foreground">{t('compositePlan.loading')}</div>}>
                    <CompositePlanTab org={org} assetUid={assetData.uid} />
                </Suspense>
            )}

            {effectiveTab === 'site-map' && assetData?.uid && (
                <Suspense fallback={<div className="flex h-[600px] items-center justify-center rounded-lg border border-border bg-card text-muted-foreground">{t('siteMap:loading')}</div>}>
                    <SiteMapTab
                        org={org}
                        siteUid={assetData.uid}
                        initialBuildingUid={typeof window !== 'undefined' ? (new URLSearchParams(window.location.search).get('building') || undefined) : undefined}
                        onBuildingParamChange={(uid) => {
                            const url = new URL(window.location.href)
                            if (uid) url.searchParams.set('building', uid)
                            else url.searchParams.delete('building')
                            window.history.replaceState({}, '', url.toString())
                        }}
                    />
                </Suspense>
            )}

            {effectiveTab === 'cadastre' && assetData?.uid && (
                <Suspense fallback={<div className="flex h-[500px] items-center justify-center rounded-lg border border-border bg-card text-muted-foreground">{t('cadastre:loading')}</div>}>
                    <CadastreMap org={org} assetUid={assetData.uid} />
                </Suspense>
            )}

            {effectiveTab === 'capex' && assetData?.uid && (
                <AssetCapexPlans org={org} assetUid={assetData.uid} />
            )}

            {effectiveTab === 'engagement' && assetData?.uid && (
                <EngagementScore
                    org={org}
                    assetUid={assetData.uid}
                    lens={new URLSearchParams(window.location.search).get('lens') || undefined}
                    onLensChange={(lens) => {
                        const url = new URL(window.location.href)
                        url.searchParams.set('lens', lens)
                        window.history.replaceState({}, '', url.toString())
                    }}
                />
            )}

            {effectiveTab === 'interventions' && assetData?.uid && (
                <InterventionsTab org={org} assetUid={assetData.uid} assetTypeCode={assetTypeCode} />
            )}

            {effectiveTab === 'equipment' && assetData?.uid && (
                <div className="space-y-6">
                    <EquipmentTab org={org} assetUid={assetData.uid} assetTypeCode={assetTypeCode} />
                    {/* What the equipment serves (spec 2026-10-25-24): the
                        networks belong with the equipment. Easements moved to
                        the Data tab, the full relations panel stays on
                        Properties. */}
                    <NetworksSection org={org} assetUid={assetData.uid} />
                </div>
            )}

            {effectiveTab === 'data' && assetData?.uid && (
                <div className="space-y-6">
                    <AssetDataTab org={org} assetUid={assetData.uid} assetTypeUid={currentAssetType?.uid} assetTypeCode={assetTypeCode} />
                    {/* Urban-planning easements, in both directions (spec
                        2026-10-25-24). */}
                    <RelationsPanel
                        org={org}
                        assetUid={assetData.uid}
                        assetTypeUid={currentAssetType?.uid}
                        relations={(assetData as any)?.relations}
                        relationTypeCodes={['easement_over']}
                        title={t('relations.easements_title')}
                        addLabel={t('relations.easements_add')}
                        emptyText={t('relations.easements_empty')}
                        testIdSuffix="easements"
                    />
                    {/* A building lists its floors and lots in the stacking
                        plan; every other type keeps its children here. */}
                    {assetTypeCode !== 'building' && <AssetChildrenSection org={org} assetUid={assetData.uid} />}
                </div>
            )}

            {effectiveTab === 'contracts' && (
                <div className="space-y-6">
                    {/* Where the building's costs stand against its portfolio (spec
                        2026-09-13-01) — on the tab where a contract is renegotiated.
                        Money, so finance.read. */}
                    {/* Who works on this asset, with or without a contract
                        (spec 2026-10-25-25): the contacts of the former right
                        rail. A provider with no contract here is held by a
                        lightweight RELATION and reads « Sans contrat ». */}
                    {assetData?.uid && (
                        <AssetContacts
                            org={org}
                            assetUid={assetData.uid}
                            title={tUsers('intervenants.title')}
                            addLabel={tUsers('intervenants.add')}
                            addVariant="button"
                            testId="asset-intervenants"
                        />
                    )}
                    {isBuildingAsset && canReadFinance && assetData?.uid && (
                        <AssetBenchmarkCard org={org} assetUid={assetData.uid} />
                    )}
                    <AssetContracts
                        org={org}
                        assetUid={assetData?.uid}
                        assetName={assetData?.name}
                        checklist={contractChecklist}
                    />
                </div>
            )}

            {effectiveTab === 'occupation' && assetData?.uid && (
                <OccupationTab org={org} assetUid={assetData.uid} assetTypeCode={assetTypeCode} isRoomInLot={isRoomInLot} />
            )}

            {effectiveTab === 'rent-roll' && assetData?.uid && (
                <AssetRentRollTab org={org} assetUid={assetData.uid} />
            )}

            {effectiveTab === 'decisions' && assetData?.uid && (
                <AssetDecisions org={org} assetUid={assetData.uid} />
            )}

            {effectiveTab === 'activity' && assetData?.uid && (
                <div className="space-y-6">
                    {/* Notes moved here from the right rail (spec
                        2026-10-25-20): what people wrote about the asset sits
                        above what happened to it. */}
                    {canReadKnowledge && <AssetNotesSection org={org} assetUid={assetData.uid} />}
                    <AssetActivityLog org={org} assetUid={assetData.uid} />
                </div>
            )}

            {effectiveTab === 'issues' && assetData?.uid && (
                <div className="space-y-6">
                    {/* The site's intake address, where the manager actually
                        works: it is meant to be printed in the AG convocation
                        and on the notice board, so it must be one click away
                        from the building rather than behind the queue. */}
                    {assetTypeCode === 'building_group' && (
                        <AssetIntakeAddress org={org} assetUid={assetData.uid} />
                    )}
                    {/* The site's or building's open claims (spec 2026-10-11-17):
                        a claim is an issue, so it sits with the issues, not
                        under the contracts (spec 2026-10-25-20). */}
                    {contractChecklist && canReadIssues && (
                        <AssetClaimsCard org={org} assetUid={assetData.uid} />
                    )}
                    <EntityIssuesList targetType="asset" targetEntityUid={assetData.uid} />
                    {/* The requests that never became tickets live here too:
                        without them the referential would remember only the
                        demandes that happened to become work. */}
                    <EntityConversations org={org} assetUid={assetData.uid} assetName={assetData.name ?? undefined} />
                </div>
            )}


            {effectiveTab === 'documents' && (
                <div className="space-y-6">
                    {/* Documents */}
                    <AssetDocuments org={org} assetUid={assetData?.uid} />
                </div>
            )}

            {effectiveTab === 'compliance' && (
                <AssetCompliance
                    org={org}
                    assetUid={assetData?.uid}
                    rollupDescendants={assetTypeCode === 'building_group'}
                />
            )}
            </div>

        </div>
    )
}
